import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { sampleBooks } from '../data/mock';
import {
  BookOpen,
  Upload,
  Users,
  Settings,
  Plus,
  Trash2,
  Eye,
  LogOut,
  Shield,
  TreePine,
  X,
  Image,
  Loader2,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  ImagePlus,
  Trophy,
  Calendar,
  ChevronDown,
  ChevronUp,
  PenLine,
  Clock,
  MessageCircle,
  Flag,
  Ban,
  Mail,
  Inbox,
  Check,
  UserPlus,
  Camera,
  ShoppingBag,
  Package,
  CreditCard,
  Download,
  Menu as MenuIcon
} from 'lucide-react';
import { toast } from 'sonner';

// Import API URL from utility
import { API_URL, getImageUrl } from '../lib/api';

// Rich text editor for email templates
const RichEmailEditor = ({ value, onChange }) => {
  const editorRef = React.useRef(null);
  const [mode, setMode] = React.useState('visual'); // 'visual' or 'html'

  const execCmd = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 bg-slate-50 border-b">
        <button type="button" onClick={() => execCmd('bold')} className="p-1.5 rounded hover:bg-slate-200 font-bold text-sm" title="Bold">B</button>
        <button type="button" onClick={() => execCmd('italic')} className="p-1.5 rounded hover:bg-slate-200 italic text-sm" title="Italic">I</button>
        <button type="button" onClick={() => execCmd('underline')} className="p-1.5 rounded hover:bg-slate-200 underline text-sm" title="Underline">U</button>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <button type="button" onClick={() => execCmd('formatBlock', 'h1')} className="p-1.5 rounded hover:bg-slate-200 text-xs font-bold" title="Heading 1">H1</button>
        <button type="button" onClick={() => execCmd('formatBlock', 'h2')} className="p-1.5 rounded hover:bg-slate-200 text-xs font-bold" title="Heading 2">H2</button>
        <button type="button" onClick={() => execCmd('formatBlock', 'p')} className="p-1.5 rounded hover:bg-slate-200 text-xs" title="Paragraph">P</button>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <button type="button" onClick={() => execCmd('insertUnorderedList')} className="p-1.5 rounded hover:bg-slate-200 text-xs" title="Bullet List">List</button>
        <button type="button" onClick={() => execCmd('justifyLeft')} className="p-1.5 rounded hover:bg-slate-200 text-xs" title="Align Left">Left</button>
        <button type="button" onClick={() => execCmd('justifyCenter')} className="p-1.5 rounded hover:bg-slate-200 text-xs" title="Center">Center</button>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <select onChange={(e) => { if (e.target.value) execCmd('foreColor', e.target.value); e.target.value = ''; }} className="text-xs border rounded px-1 py-1" defaultValue="">
          <option value="" disabled>Color</option>
          <option value="#059669" style={{color:'#059669'}}>Green</option>
          <option value="#2563eb" style={{color:'#2563eb'}}>Blue</option>
          <option value="#7c3aed" style={{color:'#7c3aed'}}>Purple</option>
          <option value="#dc2626" style={{color:'#dc2626'}}>Red</option>
          <option value="#1e293b">Dark</option>
        </select>
        <button type="button" onClick={() => { const url = prompt('Link URL:'); if (url) execCmd('createLink', url); }} className="p-1.5 rounded hover:bg-slate-200 text-xs text-blue-600" title="Add Link">Link</button>
        <span className="flex-1" />
        <button type="button" onClick={() => setMode(mode === 'visual' ? 'html' : 'visual')} className={`px-2 py-1 rounded text-xs ${mode === 'html' ? 'bg-slate-800 text-white' : 'bg-slate-200'}`}>
          {mode === 'visual' ? 'HTML' : 'Visual'}
        </button>
      </div>
      
      {/* Editor */}
      {mode === 'visual' ? (
        <div
          ref={editorRef}
          contentEditable
          className="p-4 min-h-[250px] focus:outline-none text-sm"
          style={{ fontFamily: 'Georgia, serif' }}
          dangerouslySetInnerHTML={{ __html: value || '' }}
          onInput={() => { if (editorRef.current) onChange(editorRef.current.innerHTML); }}
        />
      ) : (
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-full p-4 min-h-[250px] font-mono text-xs border-0 focus:outline-none resize-y"
          placeholder="HTML source..."
        />
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('books');
  const [showPageManager, setShowPageManager] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingPages, setUploadingPages] = useState(false);
  const [startPage, setStartPage] = useState(2);
  const [bookPages, setBookPages] = useState([]);
  const [coverImage, setCoverImage] = useState(null);
  const [allBooks, setAllBooks] = useState(sampleBooks);
  const [loadingBooks, setLoadingBooks] = useState(false);
  const [showAddBook, setShowAddBook] = useState(false);
  const [newBook, setNewBook] = useState({ title: '', author: '', description: '', tier: 'free' });
  
  // Edit book metadata state
  const [editingBookMeta, setEditingBookMeta] = useState(null);
  const [editBookForm, setEditBookForm] = useState({ title: '', author: '', description: '', tier: 'free', shop_enabled: false, physical_price: 0, early_access: false });
  
  // Communications state
  const [contactMessages, setContactMessages] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [communicationStats, setCommunicationStats] = useState({ unread_messages: 0, total_messages: 0, total_subscribers: 0 });
  const [loadingComms, setLoadingComms] = useState(false);

  const isSuper = user?.role === 'super_admin';

  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return { 'Authorization': `Bearer ${token}` };
  };

  // Fetch books from database
  const fetchBooks = useCallback(async () => {
    setLoadingBooks(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/books`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const dbBooks = await response.json();
        // Merge database books with sample books, prioritizing database data
        const mergedBooks = [...sampleBooks];
        dbBooks.forEach(dbBook => {
          const idx = mergedBooks.findIndex(b => b.id === dbBook.book_id);
          if (idx >= 0) {
            // Database book exists in samples - merge with DB taking priority
            const sampleBook = mergedBooks[idx];
            mergedBooks[idx] = {
              ...sampleBook,
              ...dbBook,
              id: dbBook.book_id,
              // Use database pages if available, otherwise fall back to sample pageImages
              pages: dbBook.pages || sampleBook.pageImages || [],
              // Use database cover or first page, fall back to sample cover
              cover: dbBook.cover_url || (dbBook.pages && dbBook.pages[0]) || sampleBook.cover
            };
          } else {
            // New book from database only
            mergedBooks.push({
              ...dbBook,
              id: dbBook.book_id,
              pages: dbBook.pages || [],
              cover: dbBook.cover_url || (dbBook.pages && dbBook.pages[0])
            });
          }
        });
        setAllBooks(mergedBooks);
      }
    } catch (err) {
      console.log('Using mock data');
    }
    setLoadingBooks(false);
  }, []);

  useEffect(() => {
    fetchBooks();
  }, [fetchBooks]);

  // Open page manager
  const openPageManager = async (book) => {
    setSelectedBook(book);
    setShowPageManager(true);
    setCoverImage(null);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/books/${book.id}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const dbBook = await response.json();
        const pages = dbBook.pages || book.pages || book.pageImages || [];
        setBookPages(pages);
        setCoverImage(pages[0] || book.cover || null);
        // Set start page to after last existing page
        setStartPage(pages.length > 0 ? pages.length + 1 : 2);
      } else {
        const pages = book.pages || book.pageImages || [];
        setBookPages(pages);
        setCoverImage(book.cover || null);
        setStartPage(pages.length > 0 ? pages.length + 1 : 2);
      }
    } catch (err) {
      const pages = book.pages || book.pageImages || [];
      setBookPages(pages);
      setCoverImage(book.cover || null);
      setStartPage(pages.length > 0 ? pages.length + 1 : 2);
    }
  };

  // Upload cover image
  const uploadCover = async (file) => {
    if (!file || !selectedBook) return;
    
    setUploadingCover(true);
    try {
      const formData = new FormData();
      formData.append('files', file);
      formData.append('start_page', '1');
      
      const response = await fetch(`${API_URL}/api/admin/books/${selectedBook.id}/pages`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success('Cover uploaded!');
        setCoverImage(result.uploaded[0]?.url);
        const newPages = [...bookPages];
        newPages[0] = result.uploaded[0]?.url;
        setBookPages(newPages);
      } else {
        toast.error('Failed to upload cover');
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploadingCover(false);
    }
  };

  // Upload internal pages
  const uploadPages = async (files) => {
    if (!files.length || !selectedBook) return;
    
    setUploadingPages(true);
    try {
      const formData = new FormData();
      for (const file of files) {
        formData.append('files', file);
      }
      formData.append('start_page', startPage.toString());
      
      const response = await fetch(`${API_URL}/api/admin/books/${selectedBook.id}/pages`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`Uploaded ${result.uploaded.length} pages!`);
        
        const newPages = [...bookPages];
        result.uploaded.forEach(({ page, url }) => {
          while (newPages.length < page) newPages.push(null);
          newPages[page - 1] = url;
        });
        setBookPages(newPages);
        setStartPage(startPage + files.length);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploadingPages(false);
    }
  };

  // Delete a page - removes it and adjusts the document
  const deletePage = async (pageNum) => {
    if (!selectedBook || !window.confirm(`Delete page ${pageNum}? All subsequent pages will shift up.`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/books/${selectedBook.id}/pages/${pageNum}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        // Remove the page from array (not set to null)
        const newPages = bookPages.filter((_, i) => i !== pageNum - 1);
        setBookPages(newPages);
        setCoverImage(newPages[0] || null);
        setStartPage(newPages.length + 1);
        toast.success(`Page ${pageNum} deleted. ${data.total_pages} pages remaining.`);
      } else {
        toast.error('Failed to delete');
      }
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  // Drag and drop reorder
  const [dragIndex, setDragIndex] = useState(null);
  
  const handleDragStart = (index) => {
    setDragIndex(index);
  };
  
  const handleDragOver = (e, index) => {
    e.preventDefault();
  };
  
  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === dropIndex) { setDragIndex(null); return; }
    
    const newPages = [...bookPages];
    const [moved] = newPages.splice(dragIndex, 1);
    newPages.splice(dropIndex, 0, moved);
    setBookPages(newPages);
    setCoverImage(newPages[0] || null);
    setDragIndex(null);
    
    try {
      await fetch(`${API_URL}/api/admin/books/${selectedBook.id}/reorder`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ pages: newPages })
      });
      toast.success('Pages reordered');
    } catch (error) {
      toast.error('Failed to save order');
    }
  };

  // Move page (button fallback for accessibility)
  const movePage = async (pageNum, direction) => {
    const fromIdx = pageNum - 1;
    const toIdx = direction === 'up' ? fromIdx - 1 : fromIdx + 1;
    if (toIdx < 0 || toIdx >= bookPages.length) return;
    
    const newPages = [...bookPages];
    [newPages[fromIdx], newPages[toIdx]] = [newPages[toIdx], newPages[fromIdx]];
    setBookPages(newPages);
    setCoverImage(newPages[0] || null);
    
    try {
      await fetch(`${API_URL}/api/admin/books/${selectedBook.id}/reorder`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ pages: newPages })
      });
      toast.success('Reordered');
    } catch (error) {
      toast.error('Failed to save order');
    }
  };

  // Delete book
  const deleteBook = async (bookId) => {
    if (!window.confirm('Delete this book permanently?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/books/${bookId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Book deleted');
        setAllBooks(allBooks.filter(b => b.id !== bookId));
      } else {
        toast.error('Failed to delete');
      }
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  // Update book metadata
  const updateBookMeta = async () => {
    if (!editingBookMeta || !editBookForm.title) {
      toast.error('Please enter a book title');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/books/${editingBookMeta}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editBookForm.title,
          author: editBookForm.author || 'Creassion Resources',
          description: editBookForm.description,
          tier: editBookForm.tier,
          shop_enabled: editBookForm.shop_enabled,
          physical_price: editBookForm.physical_price || null,
          early_access: editBookForm.early_access
        })
      });
      
      if (response.ok) {
        toast.success('Book details updated!');
        setEditingBookMeta(null);
        fetchBooks();
        
        // Update selected book if it's being viewed
        if (selectedBook && (selectedBook.id === editingBookMeta || selectedBook.book_id === editingBookMeta)) {
          setSelectedBook({
            ...selectedBook,
            title: editBookForm.title,
            author: editBookForm.author,
            description: editBookForm.description,
            tier: editBookForm.tier
          });
        }
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to update book');
      }
    } catch (error) {
      toast.error('Update failed');
    }
  };

  // Open edit book metadata modal
  const openEditBookMeta = (book) => {
    setEditingBookMeta(book.book_id || book.id);
    setEditBookForm({
      title: book.title || '',
      author: book.author || '',
      description: book.description || '',
      tier: book.tier || 'free',
      shop_enabled: book.shop_enabled || false,
      physical_price: book.physical_price || 0,
      early_access: book.early_access || false
    });
  };

  // Add new book
  const addBook = async () => {
    if (!newBook.title) {
      toast.error('Please enter a book title');
      return;
    }
    
    try {
      const bookId = `book_${newBook.title.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
      
      const response = await fetch(`${API_URL}/api/admin/books/${bookId}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: newBook.title,
          author: newBook.author || 'Creassion Resources',
          description: newBook.description,
          tier: newBook.tier,
          category: 'Faith Stories'
        })
      });
      
      if (response.ok) {
        toast.success('Book created! Now add a cover and pages.');
        setShowAddBook(false);
        setNewBook({ title: '', author: '', description: '', tier: 'free' });
        fetchBooks();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to create book');
      }
    } catch (error) {
      toast.error('Create failed: ' + error.message);
    }
  };

  // Count non-null pages - handles both array and number
  const countPages = (pages) => {
    if (Array.isArray(pages)) {
      return pages.filter(Boolean).length;
    }
    return typeof pages === 'number' ? pages : 0;
  };

  const sidebarItems = [
    { id: 'books', label: 'Books', icon: BookOpen },
    { id: 'readathon', label: 'Readathon', icon: Trophy },
    { id: 'events', label: 'Events', icon: Calendar },
    { id: 'tiers', label: 'Subscriptions', icon: CreditCard },
    { id: 'orders', label: 'Shop', icon: ShoppingBag },
    { id: 'invoices', label: 'Invoices', icon: CreditCard },
    { id: 'analytics', label: 'Analytics', icon: Trophy },
    { id: 'volunteers', label: 'Volunteers', icon: UserPlus },
    { id: 'communications', label: 'Messages', icon: Mail },
    { id: 'newsletter', label: 'Newsletter', icon: Inbox },
    { id: 'team', label: 'Team', icon: Users },
    { id: 'testimonials', label: 'Testimonials', icon: MessageCircle },
    { id: 'moderation', label: 'Moderation', icon: Shield },
    { id: 'policies', label: 'Policies', icon: Shield },
    { id: 'gdpr', label: 'GDPR', icon: Shield },
    ...(isSuper ? [{ id: 'settings', label: 'Settings', icon: Settings }] : [])
  ];

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setMobileMenuOpen(false)} />
      )}
      
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-slate-900 text-white flex flex-col transform transition-transform lg:translate-x-0 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6" />
              </div>
              <div>
                <h1 className="font-bold">Admin Panel</h1>
                <p className="text-xs text-slate-400 capitalize">{user?.role?.replace('_', ' ')}</p>
              </div>
            </div>
            <button className="lg:hidden text-slate-400" onClick={() => setMobileMenuOpen(false)}>
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-1">
          {sidebarItems.map((item) => (
            <button
              key={item.id}
              onClick={() => { setActiveTab(item.id); setMobileMenuOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                activeTab === item.id ? 'bg-emerald-500 text-white' : 'text-slate-400 hover:bg-slate-800'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </button>
          ))}
        </nav>
        
        <div className="p-4 border-t border-slate-700 space-y-1">
          <button onClick={() => navigate('/dashboard')} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-800">
            <TreePine className="w-5 h-5" /> Back to Library
          </button>
          <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-red-900 hover:text-red-400">
            <LogOut className="w-5 h-5" /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto w-full">
        <header className="bg-white border-b border-slate-200 px-4 sm:px-8 py-4 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button className="lg:hidden p-2 rounded-lg hover:bg-slate-100" onClick={() => setMobileMenuOpen(true)}>
                <MenuIcon className="w-5 h-5 text-slate-600" />
              </button>
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-slate-800 capitalize">{activeTab}</h2>
                <p className="text-xs sm:text-sm text-slate-500 hidden sm:block">Manage Creassion Resources</p>
              </div>
            </div>
            
            {activeTab === 'books' && (
              <div className="flex gap-2">
                <Button onClick={fetchBooks} variant="outline" size="sm" className="rounded-lg">
                  <RefreshCw className="w-4 h-4" />
                </Button>
                <Button onClick={() => setShowAddBook(true)} className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg">
                  <Plus className="w-4 h-4 mr-2" /> Add Book
                </Button>
              </div>
            )}
          </div>
        </header>

        <div className="p-4 sm:p-6 lg:p-8">
          {activeTab === 'books' && (
            <div className="space-y-6">
              {loadingBooks ? (
                <div className="text-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-emerald-500" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {allBooks.map((book) => (
                    <div key={book.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                      <div className="aspect-[4/3] relative bg-slate-100">
                        {book.cover ? (
                          <img src={getImageUrl(book.cover)} alt={book.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-400">
                            <BookOpen className="w-12 h-12" />
                          </div>
                        )}
                        <span className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium ${
                          book.tier === 'free' ? 'bg-emerald-100 text-emerald-700' :
                          book.tier === 'basic' ? 'bg-sky-100 text-sky-700' : 'bg-amber-100 text-amber-700'
                        }`}>{book.tier}</span>
                      </div>
                      <div className="p-4">
                        <h3 className="font-bold text-slate-800">{book.title}</h3>
                        <p className="text-sm text-slate-500">{book.author}</p>
                        <p className="text-xs text-slate-400 mt-1">{countPages(book.pages || book.pageImages || [])} pages</p>
                        
                        <div className="flex gap-2 mt-4">
                          <Button size="sm" onClick={() => navigate(`/read/${book.id}`)} variant="outline" className="flex-1">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button size="sm" onClick={() => openPageManager(book)} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white">
                            <Image className="w-4 h-4 mr-1" /> Pages
                          </Button>
                          <Button 
                            size="sm" 
                            onClick={() => openEditBookMeta(book)} 
                            variant="outline" 
                            className="text-purple-600 hover:bg-purple-50"
                            title="Edit book title, author, description and tier"
                          >
                            <Settings className="w-4 h-4" />
                          </Button>
                          {countPages(book.pages || book.pageImages || []) > 0 && (
                            <Button 
                              size="sm" 
                              onClick={async () => {
                                if (!window.confirm(`Send "New Book" email to all subscribers for "${book.title}"?`)) return;
                                try {
                                  const response = await fetch(`${API_URL}/api/admin/books/${book.book_id || book.id}/notify`, {
                                    method: 'POST',
                                    headers: getAuthHeaders()
                                  });
                                  if (response.ok) {
                                    const data = await response.json();
                                    toast.success(data.message);
                                  } else {
                                    toast.error('Failed to send notification');
                                  }
                                } catch (err) {
                                  toast.error('Failed to send notification');
                                }
                              }} 
                              variant="outline" 
                              className="text-blue-600 hover:bg-blue-50"
                              title="Notify subscribers about this book"
                            >
                              <Mail className="w-4 h-4" />
                            </Button>
                          )}
                          {isSuper && (
                            <Button size="sm" onClick={() => deleteBook(book.id)} variant="outline" className="text-red-600 hover:bg-red-50">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'readathon' && (
            <ReadathonAdmin isSuper={isSuper} getAuthHeaders={getAuthHeaders} />
          )}

          {activeTab === 'events' && (
            <EventsPanel getAuthHeaders={getAuthHeaders} />
          )}

          {activeTab === 'tiers' && (
            <TiersPanel getAuthHeaders={getAuthHeaders} />
          )}

          {activeTab === 'orders' && (
            <OrdersPanel getAuthHeaders={getAuthHeaders} />
          )}

          {activeTab === 'invoices' && (
            <InvoicesPanel getAuthHeaders={getAuthHeaders} />
          )}

          {activeTab === 'analytics' && (
            <AnalyticsPanel getAuthHeaders={getAuthHeaders} />
          )}

          {activeTab === 'volunteers' && (
            <VolunteersPanel getAuthHeaders={getAuthHeaders} />
          )}

          {activeTab === 'moderation' && (
            <ModerationPanel isSuper={isSuper} getAuthHeaders={getAuthHeaders} />
          )}

          {activeTab === 'communications' && (
            <CommunicationsPanel getAuthHeaders={getAuthHeaders} />
          )}

          {activeTab === 'newsletter' && (
            <NewsletterPanel getAuthHeaders={getAuthHeaders} />
          )}

          {activeTab === 'team' && (
            <TeamPanel getAuthHeaders={getAuthHeaders} />
          )}

          {activeTab === 'testimonials' && (
            <TestimonialsPanel getAuthHeaders={getAuthHeaders} />
          )}

          {activeTab === 'policies' && (
            <PoliciesPanel getAuthHeaders={getAuthHeaders} />
          )}

          {activeTab === 'gdpr' && (
            <GdprPanel getAuthHeaders={getAuthHeaders} />
          )}

          {activeTab === 'settings' && (
            <div className="bg-white rounded-2xl border p-8 text-center">
              <Settings className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-800">Settings</h3>
              <p className="text-slate-500">Coming soon</p>
            </div>
          )}
        </div>
      </main>

      {/* Page Manager Modal */}
      {showPageManager && selectedBook && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between bg-slate-50">
              <div>
                <h3 className="font-bold text-slate-800">{selectedBook.title}</h3>
                <p className="text-sm text-slate-500">{countPages(bookPages)} pages uploaded</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowPageManager(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Cover Upload Section */}
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                <h4 className="font-bold text-amber-800 mb-3 flex items-center gap-2">
                  <ImagePlus className="w-5 h-5" /> Cover Image
                </h4>
                <div className="flex gap-4 items-start">
                  <div className="w-32 h-40 bg-white rounded-lg border-2 border-dashed border-amber-300 overflow-hidden flex-shrink-0">
                    {coverImage ? (
                      <img src={getImageUrl(coverImage)} alt="Cover" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-amber-400">
                        <BookOpen className="w-8 h-8" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => e.target.files?.[0] && uploadCover(e.target.files[0])}
                      className="hidden"
                      id="cover-upload"
                      disabled={uploadingCover}
                    />
                    <label
                      htmlFor="cover-upload"
                      className={`inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg cursor-pointer hover:bg-amber-600 ${uploadingCover ? 'opacity-50' : ''}`}
                    >
                      {uploadingCover ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {coverImage ? 'Replace Cover' : 'Upload Cover'}
                    </label>
                    <p className="text-xs text-amber-600 mt-2">This will be page 1 (the front cover)</p>
                  </div>
                </div>
              </div>

              {/* Internal Pages Upload */}
              <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                <h4 className="font-bold text-emerald-800 mb-3 flex items-center gap-2">
                  <Upload className="w-5 h-5" /> Internal Pages
                </h4>
                <div className="flex flex-wrap gap-4 items-end">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Start at page</label>
                    <Input
                      type="number"
                      min="2"
                      value={startPage}
                      onChange={(e) => setStartPage(Math.max(2, parseInt(e.target.value) || 2))}
                      className="w-24"
                    />
                  </div>
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => e.target.files?.length && uploadPages(Array.from(e.target.files))}
                      className="hidden"
                      id="pages-upload"
                      disabled={uploadingPages}
                    />
                    <label
                      htmlFor="pages-upload"
                      className={`inline-flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg cursor-pointer hover:bg-emerald-600 ${uploadingPages ? 'opacity-50' : ''}`}
                    >
                      {uploadingPages ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      Select Images
                    </label>
                  </div>
                  <div className="text-slate-400">or</div>
                  <div>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        
                        // Warn for large files
                        if (file.size > 1024 * 1024 * 1024) {
                          toast.error('File is too large (max 1GB).');
                          e.target.value = '';
                          return;
                        }
                        
                        setUploadingPages(true);
                        toast.info('Converting PDF to pages... This may take a moment.');
                        const formData = new FormData();
                        formData.append('pdf_file', file);
                        
                        try {
                          const response = await fetch(`${API_URL}/api/books/${selectedBook.book_id || selectedBook.id}/upload-pdf`, {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` },
                            body: formData
                          });
                          
                          if (response.ok || response.status === 202) {
                            const result = await response.json();
                            if (result.status === 'converting') {
                              toast.success(`PDF uploaded! Converting ${result.total_pages} pages — refresh in a moment to see them.`);
                              // Poll for completion after a delay
                              setTimeout(async () => {
                                try {
                                  const bookRes = await fetch(`${API_URL}/api/admin/books/${selectedBook.book_id || selectedBook.id}`, { headers: getAuthHeaders() });
                                  if (bookRes.ok) {
                                    const bookData = await bookRes.json();
                                    const pages = bookData.pages || [];
                                    if (pages.length > 0) {
                                      setBookPages(pages);
                                      setCoverImage(pages[0] || null);
                                      setStartPage(pages.length + 1);
                                      toast.success(`${pages.length} pages ready!`);
                                    }
                                  }
                                } catch (e) { /* ignore poll errors */ }
                              }, 10000);
                            } else if (result.pages) {
                              toast.success(`PDF converted to ${result.total_pages} pages!`);
                              setBookPages(result.pages);
                              setCoverImage(result.pages[0] || null);
                              setStartPage(result.pages.length + 1);
                            }
                          } else {
                            const err = await response.json().catch(() => ({}));
                            toast.error(err.detail || 'PDF upload failed. Please try again.');
                          }
                        } catch (err) {
                          console.error('PDF upload error:', err);
                          toast.info('Upload is processing. Large PDFs may take a while — check the Pages tab in a moment.');
                        }
                        setUploadingPages(false);
                        e.target.value = '';
                      }}
                      className="hidden"
                      id="pdf-upload"
                      disabled={uploadingPages}
                    />
                    <label
                      htmlFor="pdf-upload"
                      className={`inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg cursor-pointer hover:bg-blue-600 ${uploadingPages ? 'opacity-50' : ''}`}
                    >
                      {uploadingPages ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookOpen className="w-4 h-4" />}
                      Upload PDF
                    </label>
                  </div>
                </div>
                <p className="text-xs text-emerald-600 mt-2">Upload images (numbered from page {startPage}) or upload a PDF to auto-convert all pages.</p>
              </div>

              {/* All Pages Grid - Drag and Drop */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold text-slate-800">All Pages ({bookPages.filter(Boolean).length} uploaded)</h4>
                  <p className="text-xs text-slate-500">Drag pages to reorder</p>
                </div>
                <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 gap-3">
                  {bookPages.filter(Boolean).map((pageUrl, i) => {
                    const pageNum = i + 1;
                    
                    return (
                      <div
                        key={`${pageUrl}-${i}`}
                        draggable
                        onDragStart={() => handleDragStart(i)}
                        onDragOver={(e) => handleDragOver(e, i)}
                        onDrop={(e) => handleDrop(e, i)}
                        className={`aspect-[3/4] rounded-lg border-2 relative overflow-hidden group cursor-grab active:cursor-grabbing transition-all ${
                          dragIndex === i ? 'opacity-40 scale-95 border-blue-400' : 'border-emerald-300 bg-white hover:border-emerald-500 hover:shadow-md'
                        }`}
                      >
                        <img src={getImageUrl(pageUrl)} alt={`Page ${pageNum}`} className="w-full h-full object-cover" />
                        
                        {/* Hover controls */}
                        <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1.5">
                          <div className="flex gap-1">
                            <button onClick={(e) => { e.stopPropagation(); movePage(pageNum, 'up'); }} disabled={i === 0} className="p-1.5 bg-white/30 rounded-lg hover:bg-white/50 disabled:opacity-20" title="Move left">
                              <ArrowUp className="w-3 h-3 text-white" />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); movePage(pageNum, 'down'); }} disabled={i === bookPages.filter(Boolean).length - 1} className="p-1.5 bg-white/30 rounded-lg hover:bg-white/50 disabled:opacity-20" title="Move right">
                              <ArrowDown className="w-3 h-3 text-white" />
                            </button>
                          </div>
                          <button onClick={(e) => { e.stopPropagation(); deletePage(pageNum); }} className="p-1.5 bg-red-500 rounded-lg hover:bg-red-600" title="Delete page">
                            <Trash2 className="w-3 h-3 text-white" />
                          </button>
                        </div>
                        
                        <span className={`absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-[10px] font-bold shadow ${
                          i === 0 ? 'bg-amber-500 text-white' : 'bg-slate-800/80 text-white'
                        }`}>
                          {i === 0 ? 'Cover' : pageNum}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            
            <div className="p-4 border-t bg-slate-50">
              <Button onClick={() => setShowPageManager(false)} className="w-full">
                Done
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Book Modal */}
      {showAddBook && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-slate-800">Add New Book</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowAddBook(false)}><X className="w-5 h-5" /></Button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
                <Input value={newBook.title} onChange={(e) => setNewBook({...newBook, title: e.target.value})} placeholder="Book title" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Author</label>
                <Input value={newBook.author} onChange={(e) => setNewBook({...newBook, author: e.target.value})} placeholder="Author name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <Textarea value={newBook.description} onChange={(e) => setNewBook({...newBook, description: e.target.value})} placeholder="Brief description" rows={3} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Access Tier</label>
                <select value={newBook.tier} onChange={(e) => setNewBook({...newBook, tier: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                  <option value="free">Free</option>
                  <option value="basic">Basic</option>
                  <option value="premium">Premium</option>
                </select>
              </div>
            </div>
            
            <div className="p-4 border-t flex gap-2">
              <Button variant="outline" onClick={() => setShowAddBook(false)} className="flex-1">Cancel</Button>
              <Button onClick={addBook} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white">Create</Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Book Metadata Modal */}
      {editingBookMeta && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <Settings className="w-5 h-5 text-purple-600" />
                Edit Book Details
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setEditingBookMeta(null)}><X className="w-5 h-5" /></Button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
                <Input 
                  value={editBookForm.title} 
                  onChange={(e) => setEditBookForm({...editBookForm, title: e.target.value})} 
                  placeholder="Book title" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Author</label>
                <Input 
                  value={editBookForm.author} 
                  onChange={(e) => setEditBookForm({...editBookForm, author: e.target.value})} 
                  placeholder="Author name" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <Textarea 
                  value={editBookForm.description} 
                  onChange={(e) => setEditBookForm({...editBookForm, description: e.target.value})} 
                  placeholder="Brief description" 
                  rows={3} 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Access Tier</label>
                <select 
                  value={editBookForm.tier} 
                  onChange={(e) => setEditBookForm({...editBookForm, tier: e.target.value})} 
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="free">Explorer (Free)</option>
                  <option value="basic">Adventurer (Basic)</option>
                  <option value="premium">Champion (Premium)</option>
                  <option value="faith_builder">Faith Builder</option>
                </select>
              </div>
              
              {/* Page Count - Read Only */}
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-800">Pages Uploaded</p>
                    <p className="text-xs text-blue-600">This updates automatically when you add or remove pages</p>
                  </div>
                  <span className="text-2xl font-bold text-blue-700">
                    {allBooks.find(b => (b.book_id || b.id) === editingBookMeta)?.pages?.filter(Boolean).length || 0}
                  </span>
                </div>
              </div>
              
              {/* Shop Settings */}
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 space-y-3">
                <h4 className="font-medium text-amber-800 text-sm">Shop Settings</h4>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={editBookForm.shop_enabled} onChange={(e) => setEditBookForm({...editBookForm, shop_enabled: e.target.checked})} className="rounded" />
                  <label className="text-sm">Available in physical book shop</label>
                </div>
                {editBookForm.shop_enabled && (
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Physical Book Price (£)</label>
                    <Input type="number" step="0.01" value={editBookForm.physical_price || ''} onChange={(e) => setEditBookForm({...editBookForm, physical_price: parseFloat(e.target.value) || 0})} placeholder="e.g., 12.99" />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={editBookForm.early_access} onChange={(e) => setEditBookForm({...editBookForm, early_access: e.target.checked})} className="rounded" />
                  <label className="text-sm">Early access (Champion + Faith Builder get it first)</label>
                </div>
              </div>
            </div>
            
            <div className="p-4 border-t flex gap-2">
              <Button variant="outline" onClick={() => setEditingBookMeta(null)} className="flex-1">Cancel</Button>
              <Button onClick={updateBookMeta} className="flex-1 bg-purple-500 hover:bg-purple-600 text-white">
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Readathon Admin Component
const ReadathonAdmin = ({ isSuper, getAuthHeaders }) => {
  const [readathons, setReadathons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingReadathon, setEditingReadathon] = useState(null);
  const [showParticipants, setShowParticipants] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [selectedJournals, setSelectedJournals] = useState(null);
  const [showQuestions, setShowQuestions] = useState(null);
  const [customQuestions, setCustomQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState({ chapter: '', question_text: '' });
  const [bibleBooks, setBibleBooks] = useState([]);
  const [selectedBibleBooks, setSelectedBibleBooks] = useState([]);
  const [newReadathon, setNewReadathon] = useState({
    title: '',
    description: '',
    books: '',
    start_date: '',
    end_date: '',
    total_chapters: 0
  });

  const fetchReadathons = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/readathons`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setReadathons(data);
      }
    } catch (err) {
      console.error('Error fetching readathons:', err);
    }
    setLoading(false);
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchReadathons();
    fetchBibleBooks();
  }, [fetchReadathons]);

  const fetchBibleBooks = async () => {
    try {
      const response = await fetch(`${API_URL}/api/bible/books`);
      if (response.ok) setBibleBooks(await response.json());
    } catch (err) { console.error('Error fetching bible books:', err); }
  };

  const createReadathon = async () => {
    if (!newReadathon.title || !newReadathon.start_date || !newReadathon.end_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Build reading plan from selected Bible books
    const books = selectedBibleBooks.map(b => b.name);
    const readingPlan = selectedBibleBooks.map(b => ({
      book: b.name,
      chapters: b.selectedChapters || Array.from({ length: b.chapters }, (_, i) => i + 1)
    }));
    const totalChapters = readingPlan.reduce((sum, p) => sum + p.chapters.length, 0);

    try {
      const response = await fetch(`${API_URL}/api/readathons`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          title: newReadathon.title,
          description: newReadathon.description,
          books,
          reading_plan: readingPlan,
          start_date: new Date(newReadathon.start_date).toISOString(),
          end_date: new Date(newReadathon.end_date).toISOString(),
          total_chapters: totalChapters
        })
      });

      if (response.ok) {
        toast.success('Readathon created!');
        setShowCreate(false);
        setNewReadathon({ title: '', description: '', books: '', start_date: '', end_date: '', total_chapters: 0 });
        setSelectedBibleBooks([]);
        fetchReadathons();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to create readathon');
      }
    } catch (err) {
      toast.error('Failed to create readathon');
    }
  };

  const openEditReadathon = (ra) => {
    setEditingReadathon(ra);
    setNewReadathon({
      title: ra.title,
      description: ra.description || '',
      books: (ra.books || []).join(', '),
      start_date: ra.start_date ? new Date(ra.start_date).toISOString().slice(0, 16) : '',
      end_date: ra.end_date ? new Date(ra.end_date).toISOString().slice(0, 16) : '',
      total_chapters: ra.total_chapters || 0
    });
    // Restore selected bible books from reading plan
    if (ra.reading_plan?.length > 0) {
      setSelectedBibleBooks(ra.reading_plan.map(p => {
        const bb = bibleBooks.find(b => b.name === p.book);
        return { ...bb, name: p.book, selectedChapters: p.chapters };
      }).filter(Boolean));
    } else {
      setSelectedBibleBooks((ra.books || []).map(name => {
        const bb = bibleBooks.find(b => b.name === name);
        return bb ? { ...bb, selectedChapters: Array.from({ length: bb.chapters }, (_, i) => i + 1) } : null;
      }).filter(Boolean));
    }
    setShowCreate(true);
  };

  const saveReadathon = async () => {
    if (!editingReadathon) { createReadathon(); return; }
    
    const books = selectedBibleBooks.map(b => b.name);
    const readingPlan = selectedBibleBooks.map(b => ({
      book: b.name,
      chapters: b.selectedChapters || Array.from({ length: b.chapters }, (_, i) => i + 1)
    }));
    const totalChapters = readingPlan.reduce((sum, p) => sum + p.chapters.length, 0);

    try {
      const response = await fetch(`${API_URL}/api/admin/readathons/${editingReadathon.readathon_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          title: newReadathon.title,
          description: newReadathon.description,
          books,
          reading_plan: readingPlan,
          start_date: new Date(newReadathon.start_date).toISOString(),
          end_date: new Date(newReadathon.end_date).toISOString(),
          total_chapters: totalChapters
        })
      });
      if (response.ok) {
        toast.success('Readathon updated!');
        setShowCreate(false);
        setEditingReadathon(null);
        setSelectedBibleBooks([]);
        fetchReadathons();
      } else {
        toast.error('Failed to update');
      }
    } catch (err) {
      toast.error('Failed to update readathon');
    }
  };

  const addBibleBook = (book) => {
    if (selectedBibleBooks.find(b => b.name === book.name)) return;
    setSelectedBibleBooks([...selectedBibleBooks, { ...book, selectedChapters: Array.from({ length: book.chapters }, (_, i) => i + 1) }]);
  };

  const removeBibleBook = (bookName) => {
    setSelectedBibleBooks(selectedBibleBooks.filter(b => b.name !== bookName));
  };

  const toggleBibleChapter = (bookName, chapter) => {
    setSelectedBibleBooks(selectedBibleBooks.map(b => {
      if (b.name !== bookName) return b;
      const current = b.selectedChapters || [];
      const updated = current.includes(chapter) ? current.filter(c => c !== chapter) : [...current, chapter].sort((a, b) => a - b);
      return { ...b, selectedChapters: updated };
    }));
  };

  const toggleStatus = async (readathonId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/readathons/${readathonId}/status`, {
        method: 'PATCH',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        fetchReadathons();
        toast.success('Status updated');
      }
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const deleteReadathon = async (readathonId) => {
    if (!window.confirm('Delete this readathon and all participant data?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/readathons/${readathonId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('Readathon deleted');
        fetchReadathons();
      }
    } catch (err) {
      toast.error('Failed to delete readathon');
    }
  };

  const fetchParticipants = async (readathonId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/readathons/${readathonId}/participants`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setParticipants(data);
        setShowParticipants(readathonId);
      }
    } catch (err) {
      toast.error('Failed to fetch participants');
    }
  };

  const fetchJournals = async (readathonId, userId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/readathons/${readathonId}/participants/${userId}/journals`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedJournals({ userId, journals: data });
      }
    } catch (err) {
      toast.error('Failed to fetch journals');
    }
  };

  const fetchQuestions = async (readathonId) => {
    try {
      const response = await fetch(`${API_URL}/api/readathons/${readathonId}/questions`);
      if (response.ok) {
        const data = await response.json();
        setCustomQuestions(data);
        setShowQuestions(readathonId);
      }
    } catch (err) {
      toast.error('Failed to fetch questions');
    }
  };

  const addQuestion = async (readathonId) => {
    if (!newQuestion.question_text.trim()) {
      toast.error('Please enter a question');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/readathons/${readathonId}/questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          chapter: newQuestion.chapter ? parseInt(newQuestion.chapter) : null,
          question_text: newQuestion.question_text
        })
      });

      if (response.ok) {
        toast.success('Question added');
        setNewQuestion({ chapter: '', question_text: '' });
        fetchQuestions(readathonId);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to add question');
      }
    } catch (err) {
      toast.error('Failed to add question');
    }
  };

  const deleteQuestion = async (readathonId, questionId) => {
    if (!window.confirm('Delete this question?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/readathons/${readathonId}/questions/${questionId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Question deleted');
        fetchQuestions(readathonId);
      }
    } catch (err) {
      toast.error('Failed to delete question');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-slate-800">Readathon Management</h3>
          <p className="text-sm text-slate-500">Create and manage Bible reading challenges</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-emerald-500 hover:bg-emerald-600 text-white">
          <Plus className="w-4 h-4 mr-2" /> Create Readathon
        </Button>
      </div>

      {/* Readathon List */}
      {readathons.length === 0 ? (
        <div className="bg-white rounded-2xl border p-8 text-center">
          <Trophy className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-slate-800">No Readathons Yet</h3>
          <p className="text-slate-500 mb-4">Create your first Bible reading challenge</p>
          <Button onClick={() => setShowCreate(true)} className="bg-emerald-500 hover:bg-emerald-600 text-white">
            <Plus className="w-4 h-4 mr-2" /> Create Readathon
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {readathons.map((ra) => (
            <div key={ra.readathon_id} className="bg-white rounded-xl border overflow-hidden">
              <div className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    ra.is_active ? 'bg-emerald-100' : 'bg-slate-100'
                  }`}>
                    <Trophy className={`w-6 h-6 ${ra.is_active ? 'text-emerald-600' : 'text-slate-400'}`} />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-800">{ra.title}</h4>
                    <p className="text-sm text-slate-500">
                      {new Date(ra.start_date).toLocaleDateString()} - {new Date(ra.end_date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    ra.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {ra.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <Button size="sm" variant="outline" onClick={() => openEditReadathon(ra)}>
                    <Settings className="w-4 h-4 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => fetchParticipants(ra.readathon_id)}>
                    <Users className="w-4 h-4 mr-1" /> Participants
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => fetchQuestions(ra.readathon_id)}>
                    <PenLine className="w-4 h-4 mr-1" /> Questions
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => toggleStatus(ra.readathon_id)}>
                    {ra.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                  {isSuper && (
                    <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => deleteReadathon(ra.readathon_id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              
              <div className="px-4 pb-4">
                <p className="text-sm text-slate-600 mb-2">{ra.description}</p>
                <div className="flex items-center gap-4 text-sm text-slate-500">
                  <span className="flex items-center gap-1">
                    <BookOpen className="w-4 h-4" />
                    {ra.books?.join(', ') || 'No books set'}
                  </span>
                  <span className="flex items-center gap-1">
                    <PenLine className="w-4 h-4" />
                    {ra.total_chapters} chapters
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-slate-800">{editingReadathon ? 'Edit Readathon' : 'Create Readathon'}</h3>
              <Button variant="ghost" size="sm" onClick={() => { setShowCreate(false); setEditingReadathon(null); setSelectedBibleBooks([]); }}><X className="w-5 h-5" /></Button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
                <Input 
                  value={newReadathon.title} 
                  onChange={(e) => setNewReadathon({...newReadathon, title: e.target.value})} 
                  placeholder="e.g., Summer Gospel Readathon 2026" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <Textarea 
                  value={newReadathon.description} 
                  onChange={(e) => setNewReadathon({...newReadathon, description: e.target.value})} 
                  placeholder="What will participants be reading?" 
                  rows={3} 
                />
              </div>
              
              {/* Bible Book Selector */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Bible Books to Read</label>
                <select 
                  onChange={(e) => {
                    const book = bibleBooks.find(b => b.name === e.target.value);
                    if (book) addBibleBook(book);
                    e.target.value = '';
                  }}
                  className="w-full px-3 py-2 border rounded-lg text-sm mb-2"
                  defaultValue=""
                >
                  <option value="" disabled>Select a Bible book to add...</option>
                  <optgroup label="Old Testament">
                    {bibleBooks.filter(b => b.testament === 'OT').map(b => (
                      <option key={b.name} value={b.name} disabled={selectedBibleBooks.some(s => s.name === b.name)}>
                        {b.name} ({b.chapters} chapters)
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="New Testament">
                    {bibleBooks.filter(b => b.testament === 'NT').map(b => (
                      <option key={b.name} value={b.name} disabled={selectedBibleBooks.some(s => s.name === b.name)}>
                        {b.name} ({b.chapters} chapters)
                      </option>
                    ))}
                  </optgroup>
                </select>
                
                {/* Selected Books with Chapter Selection */}
                {selectedBibleBooks.length > 0 && (
                  <div className="space-y-2 mt-3">
                    {selectedBibleBooks.map((book) => (
                      <div key={book.name} className="border rounded-lg p-3 bg-emerald-50 border-emerald-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-emerald-800">{book.name} — {(book.selectedChapters || []).length}/{book.chapters} chapters selected</span>
                          <button onClick={() => removeBibleBook(book.name)} className="text-red-500 hover:text-red-700">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {Array.from({ length: book.chapters }, (_, i) => i + 1).map(ch => {
                            const isSelected = (book.selectedChapters || []).includes(ch);
                            return (
                              <button
                                key={ch}
                                type="button"
                                onClick={() => toggleBibleChapter(book.name, ch)}
                                className={`w-8 h-7 text-xs rounded transition-colors ${
                                  isSelected ? 'bg-emerald-500 text-white' : 'bg-white border border-slate-200 text-slate-500 hover:border-emerald-300'
                                }`}
                              >
                                {ch}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    <p className="text-xs text-slate-500">
                      Total: {selectedBibleBooks.reduce((sum, b) => sum + (b.selectedChapters || []).length, 0)} chapters selected
                    </p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Start Date *</label>
                  <Input 
                    type="date" 
                    value={newReadathon.start_date} 
                    onChange={(e) => setNewReadathon({...newReadathon, start_date: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">End Date *</label>
                  <Input 
                    type="date" 
                    value={newReadathon.end_date} 
                    onChange={(e) => setNewReadathon({...newReadathon, end_date: e.target.value})} 
                  />
                </div>
              </div>
            </div>
            
            <div className="p-4 border-t flex gap-2">
              <Button variant="outline" onClick={() => { setShowCreate(false); setEditingReadathon(null); setSelectedBibleBooks([]); }} className="flex-1">Cancel</Button>
              <Button onClick={saveReadathon} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white">
                {editingReadathon ? 'Save Changes' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Participants Modal */}
      {showParticipants && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-slate-800">Participants ({participants.length})</h3>
              <Button variant="ghost" size="sm" onClick={() => { setShowParticipants(null); setSelectedJournals(null); }}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              {participants.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-500">No participants yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {participants.map((p) => (
                    <div key={p.user_id} className="bg-slate-50 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-medium text-slate-800">{p.user_name}</p>
                          <p className="text-xs text-slate-500">{p.user_email}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-emerald-600">{p.progress_percent}%</p>
                          <p className="text-xs text-slate-500">{p.chapters_read} chapters</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-4 text-slate-500">
                          <span>Currently: {p.current_book} Ch.{p.current_chapter}</span>
                          <span className="flex items-center gap-1">
                            <PenLine className="w-3 h-3" /> {p.journal_entries} journal entries
                          </span>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => fetchJournals(showParticipants, p.user_id)}
                        >
                          View Journals
                        </Button>
                      </div>
                      
                      {/* Progress bar */}
                      <div className="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-emerald-500 transition-all"
                          style={{ width: `${p.progress_percent}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Selected Journals */}
              {selectedJournals && (
                <div className="mt-6 border-t pt-6">
                  <h4 className="font-bold text-slate-800 mb-4">
                    Journal Entries
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => setSelectedJournals(null)}
                      className="ml-2"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </h4>
                  
                  {selectedJournals.journals.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">No journal entries yet</p>
                  ) : (
                    <div className="space-y-4">
                      {selectedJournals.journals.map((j) => (
                        <div key={j.journal_id} className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                          <div className="flex items-center justify-between mb-3">
                            <p className="font-medium text-amber-800">{j.book_name} - Chapter {j.chapter}</p>
                            <div className="flex items-center gap-2">
                              {j.is_public && <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">Public</span>}
                              <p className="text-xs text-amber-600">{new Date(j.created_at).toLocaleDateString()}</p>
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <div>
                              <p className="text-xs font-medium text-emerald-600">Favourite Part</p>
                              <p className="text-sm text-slate-700">{j.favourite_part || <span className="italic text-slate-400">Not answered</span>}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-emerald-600">What I Learned</p>
                              <p className="text-sm text-slate-700">{j.what_learned || <span className="italic text-slate-400">Not answered</span>}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-emerald-600">How I'll Apply This</p>
                              <p className="text-sm text-slate-700">{j.how_apply || <span className="italic text-slate-400">Not answered</span>}</p>
                            </div>
                            {/* Custom Question Answers */}
                            {j.custom_answers && Object.keys(j.custom_answers).length > 0 && (
                              Object.entries(j.custom_answers).map(([qId, answer]) => (
                                <div key={qId}>
                                  <p className="text-xs font-medium text-purple-600">{qId}</p>
                                  <p className="text-sm text-slate-700">{answer || <span className="italic text-slate-400">Not answered</span>}</p>
                                </div>
                              ))
                            )}
                            <div className="pt-2 border-t border-amber-200">
                              <p className="text-xs font-medium text-emerald-600">Journal Entry</p>
                              <p className="text-sm text-slate-700 whitespace-pre-wrap">{j.journal_text || <span className="italic text-slate-400">No journal text</span>}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Questions Modal */}
      {showQuestions && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-slate-800">Custom Questions</h3>
              <Button variant="ghost" size="sm" onClick={() => { setShowQuestions(null); setCustomQuestions([]); }}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              {/* Add New Question */}
              <div className="bg-emerald-50 rounded-xl p-4 mb-6">
                <h4 className="font-medium text-slate-800 mb-3">Add Custom Question</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Chapter (optional - leave blank for all chapters)
                    </label>
                    <Input
                      type="number"
                      placeholder="e.g., 3"
                      value={newQuestion.chapter}
                      onChange={(e) => setNewQuestion({ ...newQuestion, chapter: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Question</label>
                    <Textarea
                      placeholder="Enter your question..."
                      value={newQuestion.question_text}
                      onChange={(e) => setNewQuestion({ ...newQuestion, question_text: e.target.value })}
                      rows={2}
                    />
                  </div>
                  <Button 
                    onClick={() => addQuestion(showQuestions)} 
                    className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Add Question
                  </Button>
                </div>
              </div>

              {/* Existing Questions */}
              <h4 className="font-medium text-slate-800 mb-3">Existing Questions ({customQuestions.length})</h4>
              
              {customQuestions.length === 0 ? (
                <div className="text-center py-8 bg-slate-50 rounded-xl">
                  <PenLine className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-500">No custom questions yet</p>
                  <p className="text-xs text-slate-400 mt-1">Standard questions (favourite part, what learned, how to apply) are always shown</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {customQuestions.map((q) => (
                    <div key={q.question_id} className="bg-white border rounded-xl p-4 flex items-start justify-between">
                      <div>
                        <p className="font-medium text-slate-800">{q.question_text}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {q.chapter ? `Chapter ${q.chapter}` : 'All chapters'}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:bg-red-50"
                        onClick={() => deleteQuestion(showQuestions, q.question_id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Moderation Panel Component
const ModerationPanel = ({ isSuper, getAuthHeaders }) => {
  const [discussions, setDiscussions] = useState([]);
  const [bannedUsers, setBannedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState('pending');

  useEffect(() => {
    fetchDiscussions();
    fetchBannedUsers();
  }, []);

  const fetchDiscussions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/discussions`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setDiscussions(data);
      }
    } catch (err) {
      console.error('Error fetching discussions:', err);
    }
    setLoading(false);
  };

  const fetchBannedUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/banned`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setBannedUsers(data);
      }
    } catch (err) {
      console.error('Error fetching banned users:', err);
    }
  };

  const approveComment = async (commentId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/discussions/${commentId}/approve`, {
        method: 'PATCH',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('Comment approved');
        fetchDiscussions();
      }
    } catch (err) {
      toast.error('Failed to approve comment');
    }
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/discussions/${commentId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('Comment deleted');
        fetchDiscussions();
      }
    } catch (err) {
      toast.error('Failed to delete comment');
    }
  };

  const toggleFlag = async (commentId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/discussions/${commentId}/flag`, {
        method: 'PATCH',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('Comment flagged');
        fetchDiscussions();
      }
    } catch (err) {
      toast.error('Failed to flag comment');
    }
  };

  const banUser = async (userId, userName) => {
    const reason = window.prompt(`Reason for banning ${userName}?`);
    if (reason === null) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/ban?reason=${encodeURIComponent(reason)}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('User banned');
        fetchBannedUsers();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to ban user');
      }
    } catch (err) {
      toast.error('Failed to ban user');
    }
  };

  const unbanUser = async (userId) => {
    if (!window.confirm('Unban this user?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/ban`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('User unbanned');
        fetchBannedUsers();
      }
    } catch (err) {
      toast.error('Failed to unban user');
    }
  };

  const pendingComments = discussions.filter(d => !d.is_approved && !d.is_deleted);
  const approvedComments = discussions.filter(d => d.is_approved && !d.is_deleted);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-bold text-slate-800">Discussion Moderation</h3>
        <p className="text-sm text-slate-500">Review and moderate comments across all readathons</p>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-slate-200 flex-wrap">
        <button
          onClick={() => setActiveSubTab('pending')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeSubTab === 'pending'
              ? 'text-amber-600 border-b-2 border-amber-500'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Clock className="w-4 h-4 inline mr-2" />
          Pending ({pendingComments.length})
        </button>
        <button
          onClick={() => setActiveSubTab('comments')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeSubTab === 'comments'
              ? 'text-emerald-600 border-b-2 border-emerald-500'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <MessageCircle className="w-4 h-4 inline mr-2" />
          Approved ({approvedComments.length})
        </button>
        <button
          onClick={() => setActiveSubTab('flagged')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeSubTab === 'flagged'
              ? 'text-emerald-600 border-b-2 border-emerald-500'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Flag className="w-4 h-4 inline mr-2" />
          Flagged ({discussions.filter(d => d.is_flagged && !d.is_deleted).length})
        </button>
        <button
          onClick={() => setActiveSubTab('banned')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeSubTab === 'banned'
              ? 'text-emerald-600 border-b-2 border-emerald-500'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Ban className="w-4 h-4 inline mr-2" />
          Banned Users ({bannedUsers.length})
        </button>
      </div>

      {/* Pending Tab */}
      {activeSubTab === 'pending' && (
        <div className="space-y-3">
          {pendingComments.length === 0 ? (
            <div className="bg-white rounded-xl border p-8 text-center">
              <Check className="w-12 h-12 text-emerald-300 mx-auto mb-4" />
              <p className="text-slate-500">No comments pending approval</p>
            </div>
          ) : (
            pendingComments.map((comment) => (
              <div 
                key={comment.comment_id} 
                className="bg-amber-50 rounded-xl border border-amber-200 p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium text-slate-800">{comment.user_name}</span>
                      <span className="text-xs text-slate-400">{new Date(comment.created_at).toLocaleString()}</span>
                      <span className="px-2 py-0.5 bg-amber-200 text-amber-700 rounded text-xs font-medium">Pending</span>
                    </div>
                    <p className="text-slate-700">{comment.content}</p>
                    <p className="text-xs text-slate-400 mt-2">Readathon: {comment.readathon_id}</p>
                  </div>
                  
                  <div className="flex items-center gap-1 ml-4">
                    <Button
                      size="sm"
                      onClick={() => approveComment(comment.comment_id)}
                      className="bg-emerald-500 hover:bg-emerald-600 text-white"
                    >
                      <Check className="w-4 h-4 mr-1" /> Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => banUser(comment.user_id, comment.user_name)}
                      className="text-orange-600 hover:bg-orange-50"
                    >
                      <Ban className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deleteComment(comment.comment_id)}
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Comments Tab */}
      {activeSubTab === 'comments' && (
        <div className="space-y-3">
          {approvedComments.length === 0 ? (
            <div className="bg-white rounded-xl border p-8 text-center">
              <MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No approved comments yet</p>
            </div>
          ) : (
            approvedComments.map((comment) => (
              <div 
                key={comment.comment_id} 
                className={`bg-white rounded-xl border p-4 ${comment.is_flagged ? 'border-red-200 bg-red-50' : ''}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium text-slate-800">{comment.user_name}</span>
                      <span className="text-xs text-slate-400">{new Date(comment.created_at).toLocaleString()}</span>
                      {comment.is_flagged && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-600 rounded text-xs font-medium">Flagged</span>
                      )}
                    </div>
                    <p className="text-slate-700">{comment.content}</p>
                    <p className="text-xs text-slate-400 mt-2">Readathon: {comment.readathon_id}</p>
                  </div>
                  
                  <div className="flex items-center gap-1 ml-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toggleFlag(comment.comment_id)}
                      className={comment.is_flagged ? 'text-amber-600' : 'text-slate-500'}
                    >
                      <Flag className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => banUser(comment.user_id, comment.user_name)}
                      className="text-orange-600 hover:bg-orange-50"
                    >
                      <Ban className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deleteComment(comment.comment_id)}
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Flagged Tab */}
      {activeSubTab === 'flagged' && (
        <div className="space-y-3">
          {discussions.filter(d => d.is_flagged && !d.is_deleted).length === 0 ? (
            <div className="bg-white rounded-xl border p-8 text-center">
              <Flag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No flagged comments</p>
            </div>
          ) : (
            discussions.filter(d => d.is_flagged && !d.is_deleted).map((comment) => (
              <div key={comment.comment_id} className="bg-red-50 rounded-xl border border-red-200 p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium text-slate-800">{comment.user_name}</span>
                      <span className="text-xs text-slate-400">{new Date(comment.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-slate-700">{comment.content}</p>
                  </div>
                  
                  <div className="flex items-center gap-1 ml-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toggleFlag(comment.comment_id)}
                      className="text-emerald-600"
                    >
                      Unflag
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => banUser(comment.user_id, comment.user_name)}
                      className="text-orange-600 hover:bg-orange-50"
                    >
                      <Ban className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deleteComment(comment.comment_id)}
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Banned Users Tab */}
      {activeSubTab === 'banned' && (
        <div className="space-y-3">
          {bannedUsers.length === 0 ? (
            <div className="bg-white rounded-xl border p-8 text-center">
              <Ban className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No banned users</p>
            </div>
          ) : (
            bannedUsers.map((ban) => (
              <div key={ban.ban_id} className="bg-white rounded-xl border p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-800">{ban.user_name}</p>
                  <p className="text-sm text-slate-500">{ban.user_email}</p>
                  {ban.reason && <p className="text-xs text-red-600 mt-1">Reason: {ban.reason}</p>}
                  <p className="text-xs text-slate-400 mt-1">Banned: {new Date(ban.created_at).toLocaleString()}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => unbanUser(ban.user_id)}
                  className="text-emerald-600"
                >
                  Unban
                </Button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};


// Communications Panel Component
const CommunicationsPanel = ({ getAuthHeaders }) => {
  const [activeSubTab, setActiveSubTab] = useState('messages');
  const [messages, setMessages] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [stats, setStats] = useState({ unread_messages: 0, total_messages: 0, total_subscribers: 0 });
  const [loading, setLoading] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  // Use imported API_URL from parent scope (imported at top of file)

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/messages`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
    setLoading(false);
  };

  const fetchSubscribers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/subscribers`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSubscribers(data);
      }
    } catch (error) {
      console.error('Error fetching subscribers:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stats/communications`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchMessages();
    fetchSubscribers();
    fetchStats();
  }, []);

  const markAsRead = async (messageId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/messages/${messageId}/read`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setMessages(messages.map(m => m.message_id === messageId ? { ...m, is_read: true } : m));
        setStats(prev => ({ ...prev, unread_messages: Math.max(0, prev.unread_messages - 1) }));
        toast.success('Marked as read');
      }
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/messages/${messageId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setMessages(messages.filter(m => m.message_id !== messageId));
        setStats(prev => ({ ...prev, total_messages: prev.total_messages - 1 }));
        setSelectedMessage(null);
        toast.success('Message deleted');
      }
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const removeSubscriber = async (subscriberId) => {
    if (!window.confirm('Remove this subscriber?')) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/subscribers/${subscriberId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setSubscribers(subscribers.filter(s => s.subscriber_id !== subscriberId));
        setStats(prev => ({ ...prev, total_subscribers: prev.total_subscribers - 1 }));
        toast.success('Subscriber removed');
      }
    } catch (error) {
      toast.error('Failed to remove subscriber');
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-coral-100 rounded-lg flex items-center justify-center">
              <Inbox className="w-5 h-5 text-coral-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.unread_messages}</p>
              <p className="text-xs text-slate-500">Unread Messages</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.total_messages}</p>
              <p className="text-xs text-slate-500">Total Messages</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-sky-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.total_subscribers}</p>
              <p className="text-xs text-slate-500">Subscribers</p>
            </div>
          </div>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b pb-2">
        <button
          onClick={() => setActiveSubTab('messages')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeSubTab === 'messages' ? 'bg-emerald-100 text-emerald-700' : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <Inbox className="w-4 h-4 inline mr-2" />
          Messages ({messages.length})
        </button>
        <button
          onClick={() => setActiveSubTab('subscribers')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeSubTab === 'subscribers' ? 'bg-emerald-100 text-emerald-700' : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <UserPlus className="w-4 h-4 inline mr-2" />
          Subscribers ({subscribers.length})
        </button>
      </div>

      {/* Messages Tab */}
      {activeSubTab === 'messages' && (
        <div className="grid grid-cols-2 gap-4">
          {/* Message List */}
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {loading ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto" />
              </div>
            ) : messages.length === 0 ? (
              <div className="bg-white rounded-xl border p-8 text-center">
                <Inbox className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No messages yet</p>
              </div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.message_id}
                  onClick={() => setSelectedMessage(msg)}
                  className={`bg-white rounded-xl border p-4 cursor-pointer transition-colors ${
                    selectedMessage?.message_id === msg.message_id ? 'border-emerald-500 bg-emerald-50' : ''
                  } ${!msg.is_read ? 'border-l-4 border-l-coral-500' : ''}`}
                >
                  <div className="flex justify-between items-start mb-1">
                    <p className={`font-medium text-slate-800 ${!msg.is_read ? 'font-bold' : ''}`}>{msg.name}</p>
                    <span className="text-xs text-slate-400">
                      {new Date(msg.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 truncate">{msg.email}</p>
                  {msg.subject && <p className="text-sm text-slate-600 truncate mt-1">{msg.subject}</p>}
                </div>
              ))
            )}
          </div>

          {/* Message Detail */}
          <div className="bg-white rounded-xl border p-6">
            {selectedMessage ? (
              <div>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-slate-800">{selectedMessage.name}</h3>
                    <p className="text-sm text-slate-500">{selectedMessage.email}</p>
                    {selectedMessage.organisation && (
                      <p className="text-sm text-slate-500">{selectedMessage.organisation}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {!selectedMessage.is_read && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => markAsRead(selectedMessage.message_id)}
                        className="text-emerald-600"
                      >
                        <Check className="w-4 h-4 mr-1" /> Mark Read
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deleteMessage(selectedMessage.message_id)}
                      className="text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {selectedMessage.subject && (
                  <p className="text-sm font-medium text-slate-700 mb-2">Subject: {selectedMessage.subject}</p>
                )}
                <div className="bg-slate-50 rounded-lg p-4 mt-4">
                  <p className="text-slate-700 whitespace-pre-wrap">{selectedMessage.message}</p>
                </div>
                <p className="text-xs text-slate-400 mt-4">
                  Received: {new Date(selectedMessage.created_at).toLocaleString()}
                </p>
              </div>
            ) : (
              <div className="text-center py-12">
                <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Select a message to view</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Subscribers Tab */}
      {activeSubTab === 'subscribers' && (
        <div className="space-y-4">
          {/* Add Subscriber Form */}
          <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4 flex gap-3 items-end">
            <div className="flex-1">
              <label className="block text-xs font-medium text-emerald-800 mb-1">Add Subscriber Manually</label>
              <div className="flex gap-2">
                <Input
                  placeholder="email@example.com"
                  id="add-sub-email"
                  className="text-sm"
                />
                <Input
                  placeholder="Name (optional)"
                  id="add-sub-name"
                  className="text-sm max-w-[150px]"
                />
                <Button
                  size="sm"
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  onClick={async () => {
                    const email = document.getElementById('add-sub-email').value;
                    const name = document.getElementById('add-sub-name').value;
                    if (!email) { toast.error('Email required'); return; }
                    try {
                      const r = await fetch(`${API_URL}/api/admin/subscribers`, {
                        method: 'POST',
                        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email, first_name: name })
                      });
                      if (r.ok) {
                        const data = await r.json();
                        toast.success(data.message);
                        document.getElementById('add-sub-email').value = '';
                        document.getElementById('add-sub-name').value = '';
                        fetchSubscribers();
                      }
                    } catch (e) { toast.error('Failed to add'); }
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Email</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Subscribed</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {subscribers.length === 0 ? (
                <tr>
                  <td colSpan={3} className="text-center py-8">
                    <UserPlus className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No subscribers yet</p>
                  </td>
                </tr>
              ) : (
                subscribers.map((sub) => (
                  <tr key={sub.subscriber_id} className="border-b hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-800">{sub.email}</td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {new Date(sub.subscribed_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeSubscriber(sub.subscriber_id)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
};


// Newsletter Panel Component
const NewsletterPanel = ({ getAuthHeaders }) => {
  const [templates, setTemplates] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState('send');
  const [sending, setSending] = useState(false);
  
  // Email form state
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [recipientType, setRecipientType] = useState('all_subscribers');
  
  // Template editing state
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [templatesRes, subscribersRes, logsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/email-templates`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/subscribers`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/email-logs?limit=50`, { headers: getAuthHeaders() })
      ]);
      
      if (templatesRes.ok) setTemplates(await templatesRes.json());
      if (subscribersRes.ok) setSubscribers(await subscribersRes.json());
      if (logsRes.ok) setEmailLogs(await logsRes.json());
    } catch (err) {
      console.error('Error fetching newsletter data:', err);
    }
    setLoading(false);
  };

  const sendEmail = async () => {
    if (!emailSubject || !emailBody) {
      toast.error('Please fill in subject and body');
      return;
    }
    
    setSending(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/send-email`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: emailSubject,
          body_html: emailBody,
          recipient_type: recipientType
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Email sent to ${data.sent_count} recipients!`);
        setEmailSubject('');
        setEmailBody('');
        fetchData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to send email');
      }
    } catch (err) {
      toast.error('Failed to send email');
    }
    setSending(false);
  };

  const saveTemplate = async () => {
    if (!editingTemplate) return;
    
    try {
      const method = editingTemplate.template_id ? 'PUT' : 'POST';
      const url = editingTemplate.template_id 
        ? `${API_URL}/api/admin/email-templates/${editingTemplate.template_id}`
        : `${API_URL}/api/admin/email-templates`;
      
      const response = await fetch(url, {
        method,
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(editingTemplate)
      });
      
      if (response.ok) {
        toast.success('Template saved!');
        setShowTemplateEditor(false);
        setEditingTemplate(null);
        fetchData();
      }
    } catch (err) {
      toast.error('Failed to save template');
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/email-templates/${templateId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('Template deleted');
        fetchData();
      }
    } catch (err) {
      toast.error('Failed to delete template');
    }
  };

  const loadTemplate = (template) => {
    setEmailSubject(template.subject);
    setEmailBody(template.body_html);
    toast.success('Template loaded into editor');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-bold text-slate-800">Newsletter & Email</h3>
        <p className="text-sm text-slate-500">Send emails to subscribers and manage templates</p>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setActiveSubTab('send')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeSubTab === 'send'
              ? 'text-emerald-600 border-b-2 border-emerald-500'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Mail className="w-4 h-4 inline mr-2" />
          Send Email
        </button>
        <button
          onClick={() => setActiveSubTab('templates')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeSubTab === 'templates'
              ? 'text-emerald-600 border-b-2 border-emerald-500'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <PenLine className="w-4 h-4 inline mr-2" />
          Templates ({templates.length})
        </button>
        <button
          onClick={() => setActiveSubTab('logs')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeSubTab === 'logs'
              ? 'text-emerald-600 border-b-2 border-emerald-500'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Clock className="w-4 h-4 inline mr-2" />
          Sent Emails ({emailLogs.length})
        </button>
      </div>

      {/* Send Email Tab */}
      {activeSubTab === 'send' && (
        <div className="bg-white rounded-xl border p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Recipients</label>
            <select
              value={recipientType}
              onChange={(e) => setRecipientType(e.target.value)}
              className="w-full border rounded-lg px-4 py-2"
            >
              <option value="all_subscribers">All Subscribers ({subscribers.length})</option>
              <option value="explorer_only">Explorer (Free) Members</option>
              <option value="adventurer_only">Adventurer Members</option>
              <option value="champions_only">Champion Members</option>
              <option value="faith_builder_only">Faith Builder Members</option>
              <option value="paid_members">All Paid Members (Adventurer + Champion + Faith Builder)</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Subject</label>
            <Input
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              placeholder="Email subject..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Body (HTML supported)
              <span className="text-xs text-slate-400 ml-2">Use {'{first_name}'} for personalization</span>
            </label>
            <Textarea
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              placeholder="<html><body>Hello {first_name}...</body></html>"
              rows={12}
              className="font-mono text-sm"
            />
          </div>

          {/* Quick template buttons */}
          {templates.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-slate-500">Load template:</span>
              {templates.map((t) => (
                <button
                  key={t.template_id}
                  onClick={() => loadTemplate(t)}
                  className="px-3 py-1 text-sm bg-slate-100 hover:bg-slate-200 rounded-full"
                >
                  {t.name}
                </button>
              ))}
            </div>
          )}
          
          <Button
            onClick={sendEmail}
            disabled={sending}
            className="w-full bg-emerald-500 hover:bg-emerald-600"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
            Send Email
          </Button>
        </div>
      )}

      {/* Templates Tab */}
      {activeSubTab === 'templates' && (
        <div className="space-y-4">
          <Button
            onClick={() => {
              setEditingTemplate({ name: '', subject: '', body_html: '', template_type: 'newsletter' });
              setShowTemplateEditor(true);
            }}
            className="bg-emerald-500 hover:bg-emerald-600"
          >
            <Plus className="w-4 h-4 mr-2" /> New Template
          </Button>

          {templates.length === 0 ? (
            <div className="bg-white rounded-xl border p-8 text-center">
              <PenLine className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No templates yet. Create one to save time!</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {templates.map((template) => (
                <div key={template.template_id} className="bg-white rounded-xl border p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-bold text-slate-800">{template.name}</h4>
                      <p className="text-sm text-slate-500">Type: {template.template_type}</p>
                      <p className="text-sm text-slate-600 mt-1">Subject: {template.subject}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingTemplate(template);
                          setShowTemplateEditor(true);
                        }}
                      >
                        <PenLine className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteTemplate(template.template_id)}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Email Logs Tab */}
      {activeSubTab === 'logs' && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Recipient</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Subject</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Status</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Date</th>
              </tr>
            </thead>
            <tbody>
              {emailLogs.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-8 text-slate-500">No emails sent yet</td>
                </tr>
              ) : (
                emailLogs.map((log) => (
                  <tr key={log.log_id} className="border-t">
                    <td className="px-4 py-3 text-sm">{log.recipient_email}</td>
                    <td className="px-4 py-3 text-sm truncate max-w-[200px]">{log.subject}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        log.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {log.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {new Date(log.sent_at).toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Template Editor Modal */}
      {showTemplateEditor && editingTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-bold text-lg">{editingTemplate.template_id ? 'Edit Template' : 'New Template'}</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowTemplateEditor(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Template Name</label>
                <Input
                  value={editingTemplate.name}
                  onChange={(e) => setEditingTemplate({...editingTemplate, name: e.target.value})}
                  placeholder="e.g., New Book Announcement"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Type</label>
                <select
                  value={editingTemplate.template_type}
                  onChange={(e) => setEditingTemplate({...editingTemplate, template_type: e.target.value})}
                  className="w-full border rounded-lg px-4 py-2"
                >
                  <optgroup label="Newsletter">
                    <option value="newsletter">Newsletter</option>
                    <option value="new_book">New Book Notification</option>
                    <option value="welcome">Welcome Email</option>
                  </optgroup>
                  <optgroup label="Order Emails">
                    <option value="order_confirmation">Order Confirmation</option>
                    <option value="order_payment_confirmed">Payment Confirmed</option>
                    <option value="order_processed">Order Processed</option>
                    <option value="order_shipped">Order Shipped</option>
                    <option value="order_delivered">Order Delivered</option>
                  </optgroup>
                  <optgroup label="Other">
                    <option value="readathon_notification">Readathon Notification</option>
                    <option value="event_notification">Event Notification</option>
                    <option value="volunteer_status">Volunteer Status Update</option>
                  </optgroup>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Subject</label>
                <Input
                  value={editingTemplate.subject}
                  onChange={(e) => setEditingTemplate({...editingTemplate, subject: e.target.value})}
                  placeholder="Email subject..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email Content</label>
                <div className="border rounded-lg overflow-hidden bg-white">
                  <RichEmailEditor
                    value={editingTemplate.body_html}
                    onChange={(val) => setEditingTemplate({...editingTemplate, body_html: val})}
                  />
                </div>
                <div className="mt-2 p-3 bg-amber-50 rounded-lg text-xs text-amber-700 border border-amber-200">
                  <p className="font-medium mb-1">Insert these tags — they'll be replaced with real data when sent:</p>
                  <div className="grid grid-cols-2 gap-1 mt-1">
                    <p><code className="bg-amber-100 px-1 rounded">{"{{customer_name}}"}</code> — Customer's name</p>
                    <p><code className="bg-amber-100 px-1 rounded">{"{{order_ref}}"}</code> — Order reference #</p>
                    <p><code className="bg-amber-100 px-1 rounded">{"{{product_title}}"}</code> — Product name</p>
                    <p><code className="bg-amber-100 px-1 rounded">{"{{amount}}"}</code> — Price paid</p>
                    <p><code className="bg-amber-100 px-1 rounded">{"{{tracking_number}}"}</code> — Tracking #</p>
                    <p><code className="bg-amber-100 px-1 rounded">{"{{expected_delivery}}"}</code> — Delivery date</p>
                    <p><code className="bg-amber-100 px-1 rounded">{"{{shipping_address}}"}</code> — Address</p>
                    <p><code className="bg-amber-100 px-1 rounded">{"{{first_name}}"}</code> — Subscriber name</p>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 pt-4">
                <Button onClick={saveTemplate} className="flex-1 bg-emerald-500 hover:bg-emerald-600">
                  Save Template
                </Button>
                <Button variant="outline" onClick={() => setShowTemplateEditor(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// Team Panel Component
const TeamPanel = ({ getAuthHeaders }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMember, setEditingMember] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/team`, { headers: getAuthHeaders() });
      if (response.ok) {
        setMembers(await response.json());
      }
    } catch (err) {
      console.error('Error fetching team:', err);
    }
    setLoading(false);
  };

  const uploadImage = async (file) => {
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'team');
      
      const response = await fetch(`${API_URL}/api/admin/upload-image`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });
      
      if (response.ok) {
        const result = await response.json();
        setEditingMember(prev => ({...prev, image_url: result.url}));
        toast.success('Image uploaded!');
      } else {
        toast.error('Failed to upload image');
      }
    } catch (err) {
      toast.error('Upload failed');
    }
    setUploadingImage(false);
  };

  const saveMember = async () => {
    if (!editingMember || !editingMember.name || !editingMember.role) {
      toast.error('Name and role are required');
      return;
    }
    
    try {
      const method = editingMember.member_id ? 'PUT' : 'POST';
      const url = editingMember.member_id 
        ? `${API_URL}/api/admin/team/${editingMember.member_id}`
        : `${API_URL}/api/admin/team`;
      
      const response = await fetch(url, {
        method,
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(editingMember)
      });
      
      if (response.ok) {
        toast.success('Team member saved!');
        setShowEditor(false);
        setEditingMember(null);
        fetchMembers();
      }
    } catch (err) {
      toast.error('Failed to save');
    }
  };

  const deleteMember = async (memberId) => {
    if (!window.confirm('Delete this team member?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/team/${memberId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('Team member deleted');
        fetchMembers();
      }
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-bold text-slate-800">Team Members</h3>
          <p className="text-sm text-slate-500">Manage your team section on the website</p>
        </div>
        <Button
          onClick={() => {
            setEditingMember({ name: '', role: '', bio: '', image_url: '', display_order: members.length });
            setShowEditor(true);
          }}
          className="bg-emerald-500 hover:bg-emerald-600"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Team Member
        </Button>
      </div>

      {members.length === 0 ? (
        <div className="bg-white rounded-xl border p-8 text-center">
          <UserPlus className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No team members yet. Add your first!</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {members.map((member) => (
            <div key={member.member_id} className={`bg-white rounded-xl border p-4 ${!member.is_active ? 'opacity-50' : ''}`}>
              <div className="flex gap-4">
                <div className="w-20 h-20 rounded-xl bg-slate-100 overflow-hidden flex-shrink-0">
                  {member.image_url ? (
                    <img src={getImageUrl(member.image_url)} alt={member.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-400">
                      <UserPlus className="w-8 h-8" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-slate-800">{member.name}</h4>
                  <p className="text-sm text-emerald-600">{member.role}</p>
                  {member.bio && <p className="text-sm text-slate-500 mt-1 line-clamp-2">{member.bio}</p>}
                </div>
                <div className="flex flex-col gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditingMember(member);
                      setShowEditor(true);
                    }}
                  >
                    <PenLine className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => deleteMember(member.member_id)}
                    className="text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor Modal */}
      {showEditor && editingMember && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white">
              <h3 className="font-bold text-lg">{editingMember.member_id ? 'Edit Team Member' : 'Add Team Member'}</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowEditor(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-6 space-y-4">
              {/* Photo Upload */}
              <div>
                <label className="block text-sm font-medium mb-2">Photo</label>
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24 rounded-xl bg-slate-100 overflow-hidden flex-shrink-0 border-2 border-dashed border-slate-300">
                    {editingMember.image_url ? (
                      <img src={getImageUrl(editingMember.image_url)} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400">
                        <Camera className="w-8 h-8" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])}
                      className="hidden"
                      id="team-image-upload"
                      disabled={uploadingImage}
                    />
                    <label
                      htmlFor="team-image-upload"
                      className={`inline-flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg cursor-pointer hover:bg-emerald-600 ${uploadingImage ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {uploadingImage ? 'Uploading...' : 'Upload Photo'}
                    </label>
                    {editingMember.image_url && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingMember({...editingMember, image_url: ''})}
                        className="ml-2 text-red-600"
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Name *</label>
                <Input
                  value={editingMember.name}
                  onChange={(e) => setEditingMember({...editingMember, name: e.target.value})}
                  placeholder="Full name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Role *</label>
                <Input
                  value={editingMember.role}
                  onChange={(e) => setEditingMember({...editingMember, role: e.target.value})}
                  placeholder="e.g., Founder, Illustrator"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Bio</label>
                <Textarea
                  value={editingMember.bio || ''}
                  onChange={(e) => setEditingMember({...editingMember, bio: e.target.value})}
                  placeholder="Short bio..."
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Display Order</label>
                <Input
                  type="number"
                  value={editingMember.display_order || 0}
                  onChange={(e) => setEditingMember({...editingMember, display_order: parseInt(e.target.value) || 0})}
                />
              </div>
              {editingMember.member_id && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editingMember.is_active !== false}
                    onChange={(e) => setEditingMember({...editingMember, is_active: e.target.checked})}
                    className="rounded"
                  />
                  <label className="text-sm">Active (visible on website)</label>
                </div>
              )}
              <div className="flex gap-2 pt-4">
                <Button onClick={saveMember} className="flex-1 bg-emerald-500 hover:bg-emerald-600">
                  Save
                </Button>
                <Button variant="outline" onClick={() => setShowEditor(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// Testimonials Panel Component
const TestimonialsPanel = ({ getAuthHeaders }) => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [showEditor, setShowEditor] = useState(false);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/testimonials`, { headers: getAuthHeaders() });
      if (response.ok) {
        setTestimonials(await response.json());
      }
    } catch (err) {
      console.error('Error fetching testimonials:', err);
    }
    setLoading(false);
  };

  const saveTestimonial = async () => {
    if (!editingTestimonial || !editingTestimonial.author_name || !editingTestimonial.content) {
      toast.error('Author name and content are required');
      return;
    }
    
    try {
      const method = editingTestimonial.testimonial_id ? 'PUT' : 'POST';
      const url = editingTestimonial.testimonial_id 
        ? `${API_URL}/api/admin/testimonials/${editingTestimonial.testimonial_id}`
        : `${API_URL}/api/admin/testimonials`;
      
      const response = await fetch(url, {
        method,
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(editingTestimonial)
      });
      
      if (response.ok) {
        toast.success('Testimonial saved!');
        setShowEditor(false);
        setEditingTestimonial(null);
        fetchTestimonials();
      }
    } catch (err) {
      toast.error('Failed to save');
    }
  };

  const deleteTestimonial = async (testimonialId) => {
    if (!window.confirm('Delete this testimonial?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/testimonials/${testimonialId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('Testimonial deleted');
        fetchTestimonials();
      }
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-bold text-slate-800">Testimonials</h3>
          <p className="text-sm text-slate-500">Manage "What People Say" section</p>
        </div>
        <Button
          onClick={() => {
            setEditingTestimonial({ author_name: '', author_role: '', content: '', rating: 5, display_order: testimonials.length });
            setShowEditor(true);
          }}
          className="bg-emerald-500 hover:bg-emerald-600"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Testimonial
        </Button>
      </div>

      {testimonials.length === 0 ? (
        <div className="bg-white rounded-xl border p-8 text-center">
          <MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No testimonials yet. Add your first!</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {testimonials.map((testimonial) => (
            <div key={testimonial.testimonial_id} className={`bg-white rounded-xl border p-4 ${!testimonial.is_active ? 'opacity-50' : ''}`}>
              <div className="flex justify-between items-start">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-slate-100 overflow-hidden flex-shrink-0">
                    {testimonial.author_image_url ? (
                      <img src={testimonial.author_image_url} alt={testimonial.author_name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400 font-bold">
                        {testimonial.author_name.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-800">{testimonial.author_name}</h4>
                    {testimonial.author_role && <p className="text-sm text-slate-500">{testimonial.author_role}</p>}
                    <div className="flex gap-1 my-1">
                      {[...Array(5)].map((_, i) => (
                        <span key={i} className={i < testimonial.rating ? 'text-amber-400' : 'text-slate-200'}>★</span>
                      ))}
                    </div>
                    <p className="text-slate-600 mt-2 line-clamp-2">"{testimonial.content}"</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditingTestimonial(testimonial);
                      setShowEditor(true);
                    }}
                  >
                    <PenLine className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => deleteTestimonial(testimonial.testimonial_id)}
                    className="text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor Modal */}
      {showEditor && editingTestimonial && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-bold text-lg">{editingTestimonial.testimonial_id ? 'Edit Testimonial' : 'Add Testimonial'}</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowEditor(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Author Name *</label>
                <Input
                  value={editingTestimonial.author_name}
                  onChange={(e) => setEditingTestimonial({...editingTestimonial, author_name: e.target.value})}
                  placeholder="Full name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Author Role</label>
                <Input
                  value={editingTestimonial.author_role || ''}
                  onChange={(e) => setEditingTestimonial({...editingTestimonial, author_role: e.target.value})}
                  placeholder="e.g., Parent, Teacher"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Testimonial *</label>
                <Textarea
                  value={editingTestimonial.content}
                  onChange={(e) => setEditingTestimonial({...editingTestimonial, content: e.target.value})}
                  placeholder="What they said..."
                  rows={4}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Rating</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      onClick={() => setEditingTestimonial({...editingTestimonial, rating})}
                      className={`text-2xl ${rating <= editingTestimonial.rating ? 'text-amber-400' : 'text-slate-200'}`}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Author Image URL</label>
                <Input
                  value={editingTestimonial.author_image_url || ''}
                  onChange={(e) => setEditingTestimonial({...editingTestimonial, author_image_url: e.target.value})}
                  placeholder="https://..."
                />
              </div>
              {editingTestimonial.testimonial_id && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editingTestimonial.is_active !== false}
                    onChange={(e) => setEditingTestimonial({...editingTestimonial, is_active: e.target.checked})}
                    className="rounded"
                  />
                  <label className="text-sm">Active (visible on website)</label>
                </div>
              )}
              <div className="flex gap-2 pt-4">
                <Button onClick={saveTestimonial} className="flex-1 bg-emerald-500 hover:bg-emerald-600">
                  Save
                </Button>
                <Button variant="outline" onClick={() => setShowEditor(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// Events Panel Component
const EventsPanel = ({ getAuthHeaders }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [selectedEventRSVPs, setSelectedEventRSVPs] = useState(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/events`, { headers: getAuthHeaders() });
      if (response.ok) {
        setEvents(await response.json());
      }
    } catch (err) {
      console.error('Error fetching events:', err);
    }
    setLoading(false);
  };

  const saveEvent = async () => {
    if (!editingEvent || !editingEvent.title || !editingEvent.event_date) {
      toast.error('Title and date are required');
      return;
    }
    
    try {
      const method = editingEvent.event_id ? 'PUT' : 'POST';
      const url = editingEvent.event_id 
        ? `${API_URL}/api/admin/events/${editingEvent.event_id}`
        : `${API_URL}/api/admin/events`;
      
      const response = await fetch(url, {
        method,
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(editingEvent)
      });
      
      if (response.ok) {
        toast.success('Event saved!');
        setShowEditor(false);
        setEditingEvent(null);
        fetchEvents();
      }
    } catch (err) {
      toast.error('Failed to save');
    }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/events/${eventId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('Event deleted');
        fetchEvents();
      }
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const notifySubscribers = async (eventId) => {
    if (!window.confirm('Send notification to all subscribers?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/events/${eventId}/notify`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
      }
    } catch (err) {
      toast.error('Failed to send notification');
    }
  };

  const viewRSVPs = async (eventId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/events/${eventId}/rsvps`, { headers: getAuthHeaders() });
      if (response.ok) {
        const rsvps = await response.json();
        setSelectedEventRSVPs({ eventId, rsvps });
      }
    } catch (err) {
      toast.error('Failed to load RSVPs');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-bold text-slate-800">Events Management</h3>
          <p className="text-sm text-slate-500">Create and manage events, track RSVPs</p>
        </div>
        <Button
          onClick={() => {
            setEditingEvent({
              title: '',
              description: '',
              event_type: 'bible_study',
              event_date: '',
              location_type: 'physical',
              location_address: '',
              is_rsvp_required: false,
              is_published: true
            });
            setShowEditor(true);
          }}
          className="bg-emerald-500 hover:bg-emerald-600"
        >
          <Plus className="w-4 h-4 mr-2" /> New Event
        </Button>
      </div>

      {events.length === 0 ? (
        <div className="bg-white rounded-xl border p-8 text-center">
          <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No events yet. Create your first event!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {events.map((event) => (
            <div key={event.event_id} className={`bg-white rounded-xl border p-4 ${!event.is_published ? 'opacity-60 border-dashed' : ''}`}>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-bold text-slate-800">{event.title}</h4>
                    {!event.is_published && (
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">Draft</span>
                    )}
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      event.event_type === 'bible_study' ? 'bg-blue-100 text-blue-700' :
                      event.event_type === 'bible_game' ? 'bg-purple-100 text-purple-700' :
                      event.event_type === 'workshop' ? 'bg-amber-100 text-amber-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>{event.event_type?.replace('_', ' ')}</span>
                  </div>
                  <p className="text-sm text-slate-600 mb-2 line-clamp-1">{event.description}</p>
                  <div className="flex flex-wrap gap-4 text-xs text-slate-500">
                    <span><Calendar className="w-3 h-3 inline mr-1" />{new Date(event.event_date).toLocaleDateString()}</span>
                    <span><Clock className="w-3 h-3 inline mr-1" />{new Date(event.event_date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                    <span>{event.location_type === 'online' ? '🖥️ Online' : `📍 ${event.location_address || 'TBA'}`}</span>
                    {event.is_rsvp_required && (
                      <span className="text-emerald-600">RSVPs: {event.rsvp_count || 0}{event.max_participants ? `/${event.max_participants}` : ''}</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  {event.is_rsvp_required && (
                    <Button size="sm" variant="outline" onClick={() => viewRSVPs(event.event_id)}>
                      <Users className="w-4 h-4" />
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => notifySubscribers(event.event_id)} className="text-blue-600">
                    <Mail className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setEditingEvent(event); setShowEditor(true); }}>
                    <PenLine className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => deleteEvent(event.event_id)} className="text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Event Editor Modal */}
      {showEditor && editingEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-bold text-lg">{editingEvent.event_id ? 'Edit Event' : 'New Event'}</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowEditor(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title *</label>
                <Input
                  value={editingEvent.title}
                  onChange={(e) => setEditingEvent({...editingEvent, title: e.target.value})}
                  placeholder="Event title"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Event Type</label>
                  <select
                    value={editingEvent.event_type}
                    onChange={(e) => setEditingEvent({...editingEvent, event_type: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="bible_study">Bible Study</option>
                    <option value="bible_game">Bible Game</option>
                    <option value="workshop">Workshop</option>
                    <option value="party">Party</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Location Type</label>
                  <select
                    value={editingEvent.location_type}
                    onChange={(e) => setEditingEvent({...editingEvent, location_type: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="physical">Physical</option>
                    <option value="online">Online</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Date & Time *</label>
                <Input
                  type="datetime-local"
                  value={editingEvent.event_date ? new Date(editingEvent.event_date).toISOString().slice(0, 16) : ''}
                  onChange={(e) => setEditingEvent({...editingEvent, event_date: new Date(e.target.value).toISOString()})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  {editingEvent.location_type === 'online' ? 'Online Link' : 'Address'}
                </label>
                <Input
                  value={editingEvent.location_type === 'online' ? (editingEvent.online_link || '') : (editingEvent.location_address || '')}
                  onChange={(e) => setEditingEvent({
                    ...editingEvent, 
                    [editingEvent.location_type === 'online' ? 'online_link' : 'location_address']: e.target.value
                  })}
                  placeholder={editingEvent.location_type === 'online' ? 'https://zoom.us/...' : 'Full address'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <Textarea
                  value={editingEvent.description || ''}
                  onChange={(e) => setEditingEvent({...editingEvent, description: e.target.value})}
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editingEvent.is_rsvp_required}
                    onChange={(e) => setEditingEvent({...editingEvent, is_rsvp_required: e.target.checked})}
                    className="rounded"
                  />
                  <label className="text-sm">Require RSVP</label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editingEvent.is_published !== false}
                    onChange={(e) => setEditingEvent({...editingEvent, is_published: e.target.checked})}
                    className="rounded"
                  />
                  <label className="text-sm">Published</label>
                </div>
              </div>
              {editingEvent.is_rsvp_required && (
                <div>
                  <label className="block text-sm font-medium mb-2">Max Participants (optional)</label>
                  <Input
                    type="number"
                    value={editingEvent.max_participants || ''}
                    onChange={(e) => setEditingEvent({...editingEvent, max_participants: e.target.value ? parseInt(e.target.value) : null})}
                    placeholder="Leave empty for unlimited"
                  />
                </div>
              )}
              <div className="flex gap-2 pt-4">
                <Button onClick={saveEvent} className="flex-1 bg-emerald-500 hover:bg-emerald-600">
                  Save Event
                </Button>
                <Button variant="outline" onClick={() => setShowEditor(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* RSVPs Modal */}
      {selectedEventRSVPs && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-bold text-lg">RSVPs ({selectedEventRSVPs.rsvps.length})</h3>
              <Button variant="ghost" size="sm" onClick={() => setSelectedEventRSVPs(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-4">
              {selectedEventRSVPs.rsvps.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No RSVPs yet</p>
              ) : (
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-3 py-2 text-sm">Name</th>
                      <th className="text-left px-3 py-2 text-sm">Email</th>
                      <th className="text-left px-3 py-2 text-sm">Attendees</th>
                      <th className="text-left px-3 py-2 text-sm">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedEventRSVPs.rsvps.map((rsvp) => (
                      <tr key={rsvp.rsvp_id} className="border-t">
                        <td className="px-3 py-2 text-sm">{rsvp.name}</td>
                        <td className="px-3 py-2 text-sm">{rsvp.email}</td>
                        <td className="px-3 py-2 text-sm">{rsvp.num_attendees}</td>
                        <td className="px-3 py-2 text-sm text-slate-500">
                          {new Date(rsvp.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// Volunteers Panel Component
const VolunteersPanel = ({ getAuthHeaders }) => {
  const [applications, setApplications] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedApp, setSelectedApp] = useState(null);
  const [activeTab, setActiveTab] = useState('applications'); // 'applications', 'roles', or 'form'
  const [editingRole, setEditingRole] = useState(null);
  const [showRoleEditor, setShowRoleEditor] = useState(false);
  
  // Form config state
  const [formConfig, setFormConfig] = useState({
    config_id: 'default',
    fields: [
      {key: 'name', label: 'Full Name', type: 'text', placeholder: 'Your name', required: true, enabled: true},
      {key: 'email', label: 'Email', type: 'email', placeholder: 'your@email.com', required: true, enabled: true},
      {key: 'phone', label: 'Phone', type: 'tel', placeholder: 'Your phone number', required: false, enabled: true},
      {key: 'skills', label: 'Your Skills', type: 'skills', required: false, enabled: true},
      {key: 'experience_with_children', label: 'Experience with children', type: 'textarea', placeholder: 'Tell us about your experience working with children...', required: false, enabled: true},
      {key: 'teaching_story', label: 'Share a story about teaching or inspiring a child', type: 'textarea', placeholder: 'A memorable moment when you helped a child learn or grow...', required: false, enabled: true},
      {key: 'why_volunteer', label: 'Why do you want to volunteer with Creassion?', type: 'textarea', placeholder: 'Share your motivation and what you hope to contribute...', required: true, enabled: true},
      {key: 'availability', label: 'Your availability', type: 'text', placeholder: 'e.g., Weekends, Evenings, Flexible...', required: false, enabled: true}
    ],
    skill_options: ['Teaching', 'Writing', 'Illustration', 'Event Planning', 'Social Media', 'Photography', 'Video Production', 'Music', 'Administration', 'Fundraising'],
    form_title: 'Volunteer Application',
    form_description: 'Use your gifts and talents to help children discover the love of God through engaging stories and resources.'
  });
  const [savingForm, setSavingForm] = useState(false);
  const [newSkill, setNewSkill] = useState('');

  useEffect(() => {
    fetchApplications();
    fetchRoles();
    fetchFormConfig();
  }, []);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/volunteers`, { headers: getAuthHeaders() });
      if (response.ok) {
        setApplications(await response.json());
      }
    } catch (err) {
      console.error('Error fetching applications:', err);
    }
    setLoading(false);
  };

  const fetchRoles = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/volunteer-roles`, { headers: getAuthHeaders() });
      if (response.ok) {
        setRoles(await response.json());
      }
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  };

  const fetchFormConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/volunteer/form-config`);
      if (response.ok) {
        const data = await response.json();
        setFormConfig(data);
      } else {
        console.error('Form config fetch status:', response.status);
        // Set default config on failure
        setFormConfig({
          config_id: 'default',
          fields: [
            {key: 'name', label: 'Full Name', type: 'text', placeholder: 'Your name', required: true, enabled: true},
            {key: 'email', label: 'Email', type: 'email', placeholder: 'your@email.com', required: true, enabled: true},
            {key: 'phone', label: 'Phone', type: 'tel', placeholder: 'Your phone number', required: false, enabled: true},
            {key: 'skills', label: 'Your Skills', type: 'skills', required: false, enabled: true},
            {key: 'experience_with_children', label: 'Experience with children', type: 'textarea', placeholder: 'Tell us about your experience...', required: false, enabled: true},
            {key: 'teaching_story', label: 'Share a teaching story', type: 'textarea', placeholder: 'A memorable moment...', required: false, enabled: true},
            {key: 'why_volunteer', label: 'Why do you want to volunteer with Creassion?', type: 'textarea', placeholder: 'Share your motivation...', required: true, enabled: true},
            {key: 'availability', label: 'Your availability', type: 'text', placeholder: 'e.g., Weekends, Evenings...', required: false, enabled: true}
          ],
          skill_options: ['Teaching', 'Writing', 'Illustration', 'Event Planning', 'Social Media', 'Photography', 'Video Production', 'Music', 'Administration', 'Fundraising'],
          form_title: 'Volunteer Application',
          form_description: 'Use your gifts and talents to help children discover the love of God.'
        });
      }
    } catch (err) {
      console.error('Error fetching form config:', err);
      // Set default on error
      setFormConfig({
        config_id: 'default',
        fields: [
          {key: 'name', label: 'Full Name', type: 'text', placeholder: 'Your name', required: true, enabled: true},
          {key: 'email', label: 'Email', type: 'email', placeholder: 'your@email.com', required: true, enabled: true},
          {key: 'phone', label: 'Phone', type: 'tel', placeholder: 'Your phone number', required: false, enabled: true},
          {key: 'skills', label: 'Your Skills', type: 'skills', required: false, enabled: true},
          {key: 'experience_with_children', label: 'Experience with children', type: 'textarea', placeholder: 'Tell us about your experience...', required: false, enabled: true},
          {key: 'teaching_story', label: 'Share a teaching story', type: 'textarea', placeholder: 'A memorable moment...', required: false, enabled: true},
          {key: 'why_volunteer', label: 'Why do you want to volunteer with Creassion?', type: 'textarea', placeholder: 'Share your motivation...', required: true, enabled: true},
          {key: 'availability', label: 'Your availability', type: 'text', placeholder: 'e.g., Weekends, Evenings...', required: false, enabled: true}
        ],
        skill_options: ['Teaching', 'Writing', 'Illustration', 'Event Planning', 'Social Media', 'Photography', 'Video Production', 'Music', 'Administration', 'Fundraising'],
        form_title: 'Volunteer Application',
        form_description: 'Use your gifts and talents to help children discover the love of God.'
      });
    }
  };

  const saveFormConfig = async () => {
    if (!formConfig) return;
    setSavingForm(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/volunteer/form-config`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(formConfig)
      });
      if (response.ok) {
        toast.success('Form settings saved!');
        setFormConfig(await response.json());
      } else {
        toast.error('Failed to save form settings');
      }
    } catch (err) {
      toast.error('Failed to save');
    }
    setSavingForm(false);
  };

  const updateField = (idx, key, value) => {
    const fields = [...formConfig.fields];
    fields[idx] = { ...fields[idx], [key]: value };
    setFormConfig({ ...formConfig, fields });
  };

  const addFormField = () => {
    const fields = [...formConfig.fields];
    fields.push({ key: `custom_${Date.now()}`, label: 'New Field', type: 'text', placeholder: '', required: false, enabled: true });
    setFormConfig({ ...formConfig, fields });
  };

  const removeFormField = (idx) => {
    const field = formConfig.fields[idx];
    // Don't allow removing name, email, why_volunteer
    if (['name', 'email', 'why_volunteer'].includes(field.key)) {
      toast.error('This field cannot be removed');
      return;
    }
    const fields = formConfig.fields.filter((_, i) => i !== idx);
    setFormConfig({ ...formConfig, fields });
  };

  const moveField = (idx, dir) => {
    const fields = [...formConfig.fields];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= fields.length) return;
    [fields[idx], fields[newIdx]] = [fields[newIdx], fields[idx]];
    setFormConfig({ ...formConfig, fields });
  };

  const saveRole = async () => {
    if (!editingRole || !editingRole.title) {
      toast.error('Title is required');
      return;
    }
    
    try {
      const method = editingRole.role_id ? 'PUT' : 'POST';
      const url = editingRole.role_id 
        ? `${API_URL}/api/admin/volunteer-roles/${editingRole.role_id}`
        : `${API_URL}/api/admin/volunteer-roles`;
      
      const response = await fetch(url, {
        method,
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(editingRole)
      });
      
      if (response.ok) {
        toast.success('Role saved!');
        setShowRoleEditor(false);
        setEditingRole(null);
        fetchRoles();
      }
    } catch (err) {
      toast.error('Failed to save role');
    }
  };

  const deleteRole = async (roleId) => {
    if (!window.confirm('Delete this volunteer role?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/volunteer-roles/${roleId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('Role deleted');
        fetchRoles();
      }
    } catch (err) {
      toast.error('Failed to delete role');
    }
  };

  const updateStatus = async (applicationId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/volunteers/${applicationId}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        toast.success(`Status updated to ${newStatus}. Email sent to applicant.`);
        fetchApplications();
        setSelectedApp(null);
      }
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const deleteApplication = async (applicationId) => {
    if (!window.confirm('Delete this application?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/volunteers/${applicationId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('Application deleted');
        fetchApplications();
      }
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const filteredApps = filterStatus === 'all' 
    ? applications 
    : applications.filter(a => a.status === filterStatus);

  const statusColors = {
    pending: 'bg-amber-100 text-amber-700',
    approved: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-red-100 text-red-700',
    interview_scheduled: 'bg-blue-100 text-blue-700',
    training_pending: 'bg-purple-100 text-purple-700'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex gap-4 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('applications')}
          className={`pb-3 px-1 font-medium transition-colors ${
            activeTab === 'applications'
              ? 'text-emerald-600 border-b-2 border-emerald-500'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          Applications ({applications.length})
        </button>
        <button
          onClick={() => setActiveTab('roles')}
          className={`pb-3 px-1 font-medium transition-colors ${
            activeTab === 'roles'
              ? 'text-emerald-600 border-b-2 border-emerald-500'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          Volunteer Roles ({roles.length})
        </button>
        <button
          onClick={() => { setActiveTab('form'); if (!formConfig) fetchFormConfig(); }}
          className={`pb-3 px-1 font-medium transition-colors ${
            activeTab === 'form'
              ? 'text-emerald-600 border-b-2 border-emerald-500'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          Form Settings
        </button>
      </div>

      {/* Volunteer Roles Management */}
      {activeTab === 'roles' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-bold text-slate-800">Volunteer Roles</h3>
              <p className="text-sm text-slate-500">Define roles that volunteers can apply for</p>
            </div>
            <Button
              onClick={() => {
                setEditingRole({
                  title: '',
                  description: '',
                  responsibilities: [],
                  requirements: [],
                  time_commitment: '',
                  location: '',
                  is_active: true,
                  display_order: roles.length
                });
                setShowRoleEditor(true);
              }}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Role
            </Button>
          </div>

          {roles.length === 0 ? (
            <div className="bg-white rounded-xl border p-8 text-center">
              <UserPlus className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No volunteer roles defined yet. Add your first!</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {roles.map((role) => (
                <div key={role.role_id} className={`bg-white rounded-xl border p-4 ${!role.is_active ? 'opacity-50' : ''}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-bold text-slate-800">{role.title}</h4>
                        {!role.is_active && (
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs">Inactive</span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600">{role.description}</p>
                      {role.time_commitment && (
                        <p className="text-xs text-slate-500 mt-1">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {role.time_commitment}
                        </p>
                      )}
                      {role.responsibilities && role.responsibilities.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs font-medium text-slate-700">Responsibilities:</p>
                          <ul className="text-xs text-slate-500 list-disc list-inside">
                            {role.responsibilities.slice(0, 3).map((r, i) => (
                              <li key={i}>{r}</li>
                            ))}
                            {role.responsibilities.length > 3 && <li>...and {role.responsibilities.length - 3} more</li>}
                          </ul>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingRole(role);
                          setShowRoleEditor(true);
                        }}
                      >
                        <PenLine className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteRole(role.role_id)}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Role Editor Modal */}
          {showRoleEditor && editingRole && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white">
                  <h3 className="font-bold text-lg">{editingRole.role_id ? 'Edit Role' : 'Add Role'}</h3>
                  <Button variant="ghost" size="sm" onClick={() => setShowRoleEditor(false)}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Role Title *</label>
                    <Input
                      value={editingRole.title}
                      onChange={(e) => setEditingRole({...editingRole, title: e.target.value})}
                      placeholder="e.g., Sunday School Teacher"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Description</label>
                    <Textarea
                      value={editingRole.description}
                      onChange={(e) => setEditingRole({...editingRole, description: e.target.value})}
                      placeholder="Brief description of the role..."
                      rows={3}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Responsibilities (one per line)</label>
                    <Textarea
                      value={(editingRole.responsibilities || []).join('\n')}
                      onChange={(e) => setEditingRole({
                        ...editingRole, 
                        responsibilities: e.target.value.split('\n').filter(r => r.trim())
                      })}
                      placeholder="Teaching children Bible stories&#10;Preparing lesson materials&#10;Engaging students in activities"
                      rows={4}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Requirements (one per line)</label>
                    <Textarea
                      value={(editingRole.requirements || []).join('\n')}
                      onChange={(e) => setEditingRole({
                        ...editingRole, 
                        requirements: e.target.value.split('\n').filter(r => r.trim())
                      })}
                      placeholder="DBS check&#10;Previous teaching experience&#10;Passion for children's ministry"
                      rows={4}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Time Commitment</label>
                      <Input
                        value={editingRole.time_commitment}
                        onChange={(e) => setEditingRole({...editingRole, time_commitment: e.target.value})}
                        placeholder="e.g., 2-3 hours/week"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Location</label>
                      <Input
                        value={editingRole.location}
                        onChange={(e) => setEditingRole({...editingRole, location: e.target.value})}
                        placeholder="e.g., Church, Remote, Both"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editingRole.is_active !== false}
                      onChange={(e) => setEditingRole({...editingRole, is_active: e.target.checked})}
                      className="rounded"
                    />
                    <label className="text-sm">Active (visible on volunteer page)</label>
                  </div>
                  <div className="flex gap-2 pt-4">
                    <Button onClick={saveRole} className="flex-1 bg-emerald-500 hover:bg-emerald-600">
                      Save Role
                    </Button>
                    <Button variant="outline" onClick={() => setShowRoleEditor(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Applications Tab */}
      {activeTab === 'applications' && (
        <>
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div>
              <h3 className="text-lg font-bold text-slate-800">Volunteer Applications</h3>
              <p className="text-sm text-slate-500">Review and manage volunteer applications</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {['all', 'pending', 'interview_scheduled', 'approved', 'rejected'].map((status) => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    filterStatus === status
                      ? 'bg-emerald-500 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {status === 'all' ? 'All' : status.replace('_', ' ')} 
                  ({status === 'all' ? applications.length : applications.filter(a => a.status === status).length})
                </button>
              ))}
            </div>
          </div>

          {filteredApps.length === 0 ? (
            <div className="bg-white rounded-xl border p-8 text-center">
              <UserPlus className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No applications found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredApps.map((app) => (
                <div key={app.application_id} className="bg-white rounded-xl border p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-bold text-slate-800">{app.name}</h4>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[app.status] || 'bg-slate-100'}`}>
                          {app.status?.replace('_', ' ')}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600">{app.email}</p>
                      {app.current_role && <p className="text-xs text-slate-500">Current: {app.current_role}</p>}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {app.preferred_roles?.map((role) => (
                          <span key={role} className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded text-xs">
                            {role.replace('_', ' ')}
                          </span>
                        ))}
                      </div>
                      <p className="text-xs text-slate-400 mt-2">Applied: {new Date(app.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button size="sm" variant="outline" onClick={() => setSelectedApp(app)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => deleteApplication(app.application_id)} className="text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
        </>
      )}

      {/* Form Settings Tab */}
      {activeTab === 'form' && (
        <div className="bg-white rounded-xl border p-6" data-testid="form-settings-panel">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h3 className="text-lg font-bold text-slate-800">Application Form Settings</h3>
              <p className="text-sm text-slate-500">Customize the fields shown on the volunteer application form</p>
            </div>
            <Button
              onClick={saveFormConfig}
              disabled={savingForm}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="save-form-config-btn"
            >
              {savingForm ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>
          
          <div className="grid gap-4 md:grid-cols-2 mb-6 p-4 bg-slate-50 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Form Title</label>
              <Input
                value={formConfig.form_title || ''}
                onChange={(e) => setFormConfig({ ...formConfig, form_title: e.target.value })}
                placeholder="Volunteer Application"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Form Description</label>
              <Input
                value={formConfig.form_description || ''}
                onChange={(e) => setFormConfig({ ...formConfig, form_description: e.target.value })}
                placeholder="Brief description..."
              />
            </div>
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex justify-between items-center">
              <h4 className="font-bold text-slate-800">Form Fields</h4>
              <Button size="sm" variant="outline" onClick={addFormField}>
                <Plus className="w-4 h-4 mr-1" /> Add Field
              </Button>
            </div>
            
            {formConfig.fields.map((field, idx) => (
              <div key={field.key} className={`border rounded-lg p-4 ${field.enabled ? 'border-emerald-200 bg-white' : 'border-slate-200 bg-slate-50 opacity-60'}`}>
                <div className="flex items-start gap-3">
                  <div className="flex flex-col gap-1 pt-7">
                    <button onClick={() => moveField(idx, -1)} disabled={idx === 0} className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30">
                      <ArrowUp className="w-3 h-3" />
                    </button>
                    <button onClick={() => moveField(idx, 1)} disabled={idx === formConfig.fields.length - 1} className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30">
                      <ArrowDown className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="flex-1 grid gap-3 md:grid-cols-3">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Label</label>
                      <Input value={field.label} onChange={(e) => updateField(idx, 'label', e.target.value)} className="text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Placeholder</label>
                      <Input value={field.placeholder || ''} onChange={(e) => updateField(idx, 'placeholder', e.target.value)} className="text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Type</label>
                      <select value={field.type} onChange={(e) => updateField(idx, 'type', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm">
                        <option value="text">Text</option>
                        <option value="email">Email</option>
                        <option value="tel">Phone</option>
                        <option value="textarea">Text Area</option>
                        <option value="skills">Skills Selector</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 pt-7">
                    <label className="flex items-center gap-1 text-xs whitespace-nowrap">
                      <input type="checkbox" checked={field.required} onChange={(e) => updateField(idx, 'required', e.target.checked)} className="rounded" />
                      Req
                    </label>
                    <label className="flex items-center gap-1 text-xs whitespace-nowrap">
                      <input type="checkbox" checked={field.enabled} onChange={(e) => updateField(idx, 'enabled', e.target.checked)} className="rounded" />
                      On
                    </label>
                    {!['name', 'email', 'why_volunteer'].includes(field.key) && (
                      <button onClick={() => removeFormField(idx)} className="p-1 text-red-500 hover:bg-red-50 rounded">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="p-4 bg-slate-50 rounded-lg">
            <h4 className="font-bold text-slate-800 mb-3">Skill Options</h4>
            <p className="text-xs text-slate-500 mb-3">Tags shown when Skills Selector field is enabled</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {(formConfig.skill_options || []).map((skill, idx) => (
                <span key={idx} className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm">
                  {skill}
                  <button onClick={() => setFormConfig({ ...formConfig, skill_options: formConfig.skill_options.filter((_, i) => i !== idx) })} className="ml-1 hover:text-red-600">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                placeholder="Add a skill..."
                className="max-w-xs text-sm"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newSkill.trim()) {
                    e.preventDefault();
                    setFormConfig({ ...formConfig, skill_options: [...(formConfig.skill_options || []), newSkill.trim()] });
                    setNewSkill('');
                  }
                }}
              />
              <Button size="sm" variant="outline" onClick={() => {
                if (newSkill.trim()) {
                  setFormConfig({ ...formConfig, skill_options: [...(formConfig.skill_options || []), newSkill.trim()] });
                  setNewSkill('');
                }
              }}>Add</Button>
            </div>
          </div>
        </div>
      )}

      {/* Application Detail Modal */}
      {selectedApp && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-bold text-lg">Application Details</h3>
              <Button variant="ghost" size="sm" onClick={() => setSelectedApp(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-500">Name</label>
                  <p className="font-medium">{selectedApp.name}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Email</label>
                  <p className="font-medium">{selectedApp.email}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Phone</label>
                  <p className="font-medium">{selectedApp.phone || '-'}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Current Role</label>
                  <p className="font-medium">{selectedApp.current_role || '-'}</p>
                </div>
              </div>

              <div>
                <label className="text-xs text-slate-500">Skills</label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {selectedApp.skills?.length > 0 ? selectedApp.skills.map((skill) => (
                    <span key={skill} className="px-2 py-1 bg-slate-100 rounded text-sm">{skill}</span>
                  )) : <span className="text-slate-400">-</span>}
                </div>
              </div>

              <div>
                <label className="text-xs text-slate-500">Preferred Roles</label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {selectedApp.preferred_roles?.length > 0 ? selectedApp.preferred_roles.map((role) => (
                    <span key={role} className="px-2 py-1 bg-emerald-50 text-emerald-700 rounded text-sm">{role.replace('_', ' ')}</span>
                  )) : <span className="text-slate-400">-</span>}
                </div>
              </div>

              <div>
                <label className="text-xs text-slate-500">Experience with Children</label>
                <p className="text-sm mt-1 bg-slate-50 p-3 rounded">{selectedApp.experience_with_children || '-'}</p>
              </div>

              <div>
                <label className="text-xs text-slate-500">Teaching/Guiding Story</label>
                <p className="text-sm mt-1 bg-slate-50 p-3 rounded">{selectedApp.teaching_story || '-'}</p>
              </div>

              <div>
                <label className="text-xs text-slate-500">Why Volunteer</label>
                <p className="text-sm mt-1 bg-slate-50 p-3 rounded">{selectedApp.why_volunteer}</p>
              </div>

              <div className="border-t pt-4">
                <label className="text-xs text-slate-500 block mb-2">Update Status</label>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    size="sm" 
                    onClick={() => updateStatus(selectedApp.application_id, 'interview_scheduled')}
                    className="bg-blue-500 hover:bg-blue-600"
                  >
                    Schedule Interview
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={() => updateStatus(selectedApp.application_id, 'approved')}
                    className="bg-emerald-500 hover:bg-emerald-600"
                  >
                    <Check className="w-4 h-4 mr-1" /> Approve
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={() => updateStatus(selectedApp.application_id, 'rejected')}
                    variant="outline"
                    className="text-red-600 hover:bg-red-50"
                  >
                    Reject
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={() => updateStatus(selectedApp.application_id, 'training_pending')}
                    variant="outline"
                    className="text-purple-600 hover:bg-purple-50"
                  >
                    Training Pending
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      )}
    </div>
  );
};


// Policies Panel
const PoliciesPanel = ({ getAuthHeaders }) => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  useEffect(() => { fetchPolicies(); }, []);

  const fetchPolicies = async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/policies`, { headers: getAuthHeaders() });
      if (r.ok) setPolicies(await r.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const savePolicy = async () => {
    if (!editing?.slug || !editing?.title) { toast.error('Slug and title required'); return; }
    try {
      const r = await fetch(`${API_URL}/api/admin/policies/${editing.slug}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(editing)
      });
      if (r.ok) { toast.success('Policy saved!'); setEditing(null); fetchPolicies(); }
    } catch (e) { toast.error('Failed to save'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-bold text-slate-800">Website Policies</h3>
          <p className="text-sm text-slate-500">Edit your privacy policy, terms, refund policy and more</p>
        </div>
        <Button onClick={() => setEditing({ slug: '', title: '', content: '', sort_order: policies.length, is_published: true })} className="bg-emerald-500 hover:bg-emerald-600">
          <Plus className="w-4 h-4 mr-2" /> Add Policy
        </Button>
      </div>

      <div className="space-y-3">
        {policies.map((p) => (
          <div key={p.slug} className="bg-white rounded-xl border p-4 flex items-center justify-between">
            <div>
              <h4 className="font-bold text-slate-800">{p.title}</h4>
              <p className="text-xs text-slate-500">/policy/{p.slug} {!p.is_published && <span className="text-amber-600 ml-2">(Draft)</span>}</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setEditing({...p})}>
                <PenLine className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => window.open(`/policy/${p.slug}`, '_blank')}>
                <Eye className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Editor Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
              <h3 className="font-bold">{editing.slug ? 'Edit Policy' : 'New Policy'}</h3>
              <Button variant="ghost" size="sm" onClick={() => setEditing(null)}><X className="w-5 h-5" /></Button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Title *</label>
                  <Input value={editing.title} onChange={(e) => setEditing({...editing, title: e.target.value})} placeholder="Privacy Policy" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">URL Slug *</label>
                  <Input value={editing.slug} onChange={(e) => setEditing({...editing, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')})} placeholder="privacy-policy" disabled={policies.some(p => p.slug === editing.slug)} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Content</label>
                <p className="text-xs text-slate-500 mb-2">Use **bold text** for headings. Each line becomes a paragraph.</p>
                <textarea
                  value={editing.content || ''}
                  onChange={(e) => setEditing({...editing, content: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm min-h-[400px] font-mono"
                  placeholder="Write your policy content here..."
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={editing.is_published !== false} onChange={(e) => setEditing({...editing, is_published: e.target.checked})} className="rounded" />
                  Published
                </label>
                <div>
                  <label className="text-sm mr-2">Sort Order</label>
                  <Input type="number" value={editing.sort_order || 0} onChange={(e) => setEditing({...editing, sort_order: parseInt(e.target.value) || 0})} className="w-20 inline" />
                </div>
              </div>
            </div>
            <div className="p-4 border-t flex gap-2 sticky bottom-0 bg-white">
              <Button variant="outline" onClick={() => setEditing(null)} className="flex-1">Cancel</Button>
              <Button onClick={savePolicy} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white">Save Policy</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// Tiers Management Panel
const TiersPanel = ({ getAuthHeaders }) => {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [viewingSubs, setViewingSubs] = useState(null);
  const [assignEmail, setAssignEmail] = useState('');
  const [assignTier, setAssignTier] = useState('basic');
  const [assignNote, setAssignNote] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [showAssigned, setShowAssigned] = useState(false);

  useEffect(() => { fetchTiers(); fetchAssigned(); }, []);

  const fetchTiers = async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/tiers`, { headers: getAuthHeaders() });
      if (r.ok) setTiers(await r.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchAssigned = async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/assigned-memberships`, { headers: getAuthHeaders() });
      if (r.ok) setAssignedUsers(await r.json());
    } catch (e) { console.error(e); }
  };

  const assignMembership = async () => {
    if (!assignEmail || !assignEmail.includes('@')) { toast.error('Enter a valid email'); return; }
    setAssigning(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/assign-membership`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: assignEmail, tier: assignTier, note: assignNote })
      });
      if (r.ok) {
        const data = await r.json();
        toast.success(data.message);
        setAssignEmail(''); setAssignNote('');
        fetchAssigned();
      } else {
        const err = await r.json();
        toast.error(err.detail || 'Failed to assign');
      }
    } catch (e) { toast.error('Failed to assign'); }
    setAssigning(false);
  };

  const saveTier = async () => {
    if (!editing?.tier_id || !editing?.name) { toast.error('ID and name required'); return; }
    try {
      const r = await fetch(`${API_URL}/api/admin/tiers/${editing.tier_id}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(editing)
      });
      if (r.ok) { toast.success('Tier saved!'); setEditing(null); fetchTiers(); }
    } catch (e) { toast.error('Failed to save'); }
  };

  const viewSubscribers = async (tierId) => {
    setViewingSubs(tierId);
    try {
      const r = await fetch(`${API_URL}/api/admin/tiers/${tierId}/subscribers`, { headers: getAuthHeaders() });
      if (r.ok) setSubscribers(await r.json());
    } catch (e) { console.error(e); }
  };

  const exportSubscribers = async (tierId) => {
    try {
      const r = await fetch(`${API_URL}/api/admin/tiers/${tierId}/export`, { headers: getAuthHeaders() });
      if (r.ok) {
        const data = await r.json();
        const headers = ['name','email','tier','address_line1','address_line2','city','postcode','country','phone'];
        const csv = [headers.join(','), ...data.map(row => headers.map(h => `"${(row[h]||'').toString().replace(/"/g,'""')}"`).join(','))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `${tierId}_subscribers.csv`; a.click();
        toast.success('CSV exported!');
      }
    } catch (e) { toast.error('Export failed'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-bold text-slate-800">Membership Tiers</h3>
          <p className="text-sm text-slate-500">Create, edit, and manage membership plans</p>
        </div>
        <Button onClick={() => setEditing({ tier_id: '', name: '', price: 0, currency: 'gbp', description: '', features: [], sort_order: tiers.length, is_active: true })} className="bg-emerald-500 hover:bg-emerald-600">
          <Plus className="w-4 h-4 mr-2" /> Add Tier
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {tiers.map((tier) => (
          <div key={tier.tier_id} className="bg-white rounded-xl border p-5">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h4 className="font-bold text-slate-800">{tier.name}</h4>
                <p className="text-emerald-600 font-bold">{tier.price === 0 ? 'Free' : `£${tier.price}/mo`}</p>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-xs ${tier.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                {tier.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <p className="text-sm text-slate-600 mb-3">{tier.description}</p>
            <ul className="space-y-1 mb-4">
              {(tier.features || []).map((f, i) => (
                <li key={i} className="text-xs text-slate-500 flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" />{f}</li>
              ))}
            </ul>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setEditing({...tier})}><PenLine className="w-4 h-4" /></Button>
              <Button size="sm" variant="outline" onClick={() => viewSubscribers(tier.tier_id)}><Users className="w-4 h-4 mr-1" /> Subscribers</Button>
              <Button size="sm" variant="outline" onClick={() => exportSubscribers(tier.tier_id)}><Download className="w-4 h-4 mr-1" /> Export</Button>
            </div>
          </div>
        ))}
      </div>

      {/* Assign Free Membership */}
      <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-5">
        <h4 className="font-bold text-emerald-800 mb-1">Assign Free Membership</h4>
        <p className="text-sm text-slate-500 mb-4">Give someone free access to a membership tier. Enter their Gmail and select a tier.</p>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-slate-600 mb-1">Email (Gmail) *</label>
            <Input value={assignEmail} onChange={(e) => setAssignEmail(e.target.value)} placeholder="person@gmail.com" />
          </div>
          <div className="w-40">
            <label className="block text-xs font-medium text-slate-600 mb-1">Tier</label>
            <select value={assignTier} onChange={(e) => setAssignTier(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm">
              {tiers.filter(t => t.tier_id !== 'free').map(t => (
                <option key={t.tier_id} value={t.tier_id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[150px]">
            <label className="block text-xs font-medium text-slate-600 mb-1">Note (optional)</label>
            <Input value={assignNote} onChange={(e) => setAssignNote(e.target.value)} placeholder="e.g., Partner, VIP" />
          </div>
          <Button onClick={assignMembership} disabled={assigning} className="bg-emerald-500 hover:bg-emerald-600 text-white">
            {assigning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
            Assign
          </Button>
        </div>
      </div>

      {/* Assigned Members List */}
      {assignedUsers.length > 0 && (
        <div className="bg-white rounded-xl border">
          <button onClick={() => setShowAssigned(!showAssigned)} className="w-full p-4 flex items-center justify-between text-left hover:bg-slate-50">
            <h4 className="font-bold text-slate-800">Assigned Memberships ({assignedUsers.length})</h4>
            <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${showAssigned ? 'rotate-180' : ''}`} />
          </button>
          {showAssigned && (
            <div className="px-4 pb-4 space-y-2">
              {assignedUsers.map(u => (
                <div key={u.user_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-800">{u.email}</p>
                    <p className="text-xs text-slate-500">{u.name} {u.tier_note && `— ${u.tier_note}`}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium capitalize">{u.tier}</span>
                    <p className="text-xs text-slate-400">{u.tier_assigned_at ? new Date(u.tier_assigned_at).toLocaleDateString() : ''}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Edit Tier Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-bold">{editing.tier_id ? 'Edit Tier' : 'New Tier'}</h3>
              <Button variant="ghost" size="sm" onClick={() => setEditing(null)}><X className="w-5 h-5" /></Button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Tier ID (unique key)</label>
                <Input value={editing.tier_id} onChange={(e) => setEditing({...editing, tier_id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_')})} placeholder="e.g., faith_builder" disabled={tiers.some(t => t.tier_id === editing.tier_id)} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <Input value={editing.name} onChange={(e) => setEditing({...editing, name: e.target.value})} placeholder="Tier name" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Price (monthly)</label>
                  <Input type="number" step="0.01" value={editing.price} onChange={(e) => setEditing({...editing, price: parseFloat(e.target.value) || 0})} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Sort Order</label>
                  <Input type="number" value={editing.sort_order || 0} onChange={(e) => setEditing({...editing, sort_order: parseInt(e.target.value) || 0})} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <Input value={editing.description || ''} onChange={(e) => setEditing({...editing, description: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Stripe Price ID (optional)</label>
                <Input value={editing.stripe_price_id || ''} onChange={(e) => setEditing({...editing, stripe_price_id: e.target.value})} placeholder="price_..." />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Features (one per line)</label>
                <textarea
                  value={(editing.features || []).join('\n')}
                  onChange={(e) => setEditing({...editing, features: e.target.value.split('\n').filter(Boolean)})}
                  className="w-full border rounded-lg px-3 py-2 text-sm min-h-[100px]"
                  placeholder="Feature 1&#10;Feature 2&#10;Feature 3"
                />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={editing.is_active !== false} onChange={(e) => setEditing({...editing, is_active: e.target.checked})} className="rounded" />
                <label className="text-sm">Active (visible to users)</label>
              </div>
            </div>
            <div className="p-4 border-t flex gap-2">
              <Button variant="outline" onClick={() => setEditing(null)} className="flex-1">Cancel</Button>
              <Button onClick={saveTier} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white">Save</Button>
            </div>
          </div>
        </div>
      )}

      {/* Subscribers Modal */}
      {viewingSubs && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-bold">{viewingSubs} Subscribers ({subscribers.length})</h3>
              <Button variant="ghost" size="sm" onClick={() => { setViewingSubs(null); setSubscribers([]); }}><X className="w-5 h-5" /></Button>
            </div>
            <div className="p-4">
              {subscribers.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No subscribers for this tier</p>
              ) : (
                <div className="space-y-2">
                  {subscribers.map((u) => (
                    <div key={u.user_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium text-slate-800">{u.name}</p>
                        <p className="text-xs text-slate-500">{u.email}</p>
                      </div>
                      {u.shipping_address && (
                        <div className="text-right text-xs text-slate-500">
                          <p>{u.shipping_address.line1}</p>
                          <p>{u.shipping_address.city}, {u.shipping_address.postcode}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// Orders Panel
const OrdersPanel = ({ getAuthHeaders }) => {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState('orders');
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [shippingConfig, setShippingConfig] = useState(null);
  const [savingShipping, setSavingShipping] = useState(false);
  const [newOrder, setNewOrder] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [ordRes, prodRes, catRes, shipRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/orders`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/shop/products`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/shop/categories`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/shop/shipping-rates`)
      ]);
      if (ordRes.ok) setOrders(await ordRes.json());
      if (prodRes.ok) setProducts(await prodRes.json());
      if (catRes.ok) setCategories(await catRes.json());
      if (shipRes.ok) setShippingConfig(await shipRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const saveShippingRates = async () => {
    if (!shippingConfig) return;
    setSavingShipping(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/shop/shipping-rates`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(shippingConfig)
      });
      if (r.ok) { toast.success('Shipping rates saved!'); setShippingConfig(await r.json()); }
      else toast.error('Failed to save');
    } catch (e) { toast.error('Failed to save'); }
    setSavingShipping(false);
  };

  const updateShippingRate = (region, field, value) => {
    setShippingConfig(prev => ({
      ...prev,
      rates: { ...prev.rates, [region]: { ...prev.rates[region], [field]: field === 'cost' ? parseFloat(value) || 0 : value } }
    }));
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>;

  const saveProduct = async () => {
    if (!editingProduct?.title) { toast.error('Title required'); return; }
    const pid = editingProduct.product_id || `prod_${Date.now()}`;
    try {
      const r = await fetch(`${API_URL}/api/admin/shop/products/${pid}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...editingProduct, product_id: pid, type: 'custom', is_active: editingProduct.is_active !== false })
      });
      if (r.ok) { toast.success('Product saved!'); setEditingProduct(null); fetchAll(); }
    } catch (e) { toast.error('Failed to save'); }
  };

  const deleteProduct = async (pid) => {
    if (!window.confirm('Delete this product?')) return;
    await fetch(`${API_URL}/api/admin/shop/products/${pid}`, { method: 'DELETE', headers: getAuthHeaders() });
    toast.success('Deleted'); fetchAll();
  };

  const saveCategory = async () => {
    if (!editingCategory?.name) { toast.error('Name required'); return; }
    const slug = editingCategory.slug || editingCategory.name.toLowerCase().replace(/[^a-z0-9]/g, '-');
    try {
      await fetch(`${API_URL}/api/admin/shop/categories/${slug}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...editingCategory, slug, is_active: true })
      });
      toast.success('Category saved!'); setEditingCategory(null); fetchAll();
    } catch (e) { toast.error('Failed to save'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex gap-4 border-b border-slate-200">
        {['orders', 'products', 'categories', 'shipping'].map(tab => (
          <button key={tab} onClick={() => setSubTab(tab)}
            className={`pb-3 px-1 font-medium capitalize transition-colors ${
              subTab === tab ? 'text-emerald-600 border-b-2 border-emerald-500' : 'text-slate-500 hover:text-slate-700'
            }`}>
            {tab} ({tab === 'orders' ? orders.length : tab === 'products' ? products.length : categories.length})
          </button>
        ))}
      </div>

      {/* Orders */}
      {subTab === 'orders' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-bold text-slate-800">Shop Orders ({orders.length})</h3>
            <Button onClick={() => setNewOrder({ product_title: '', customer_name: '', customer_email: '', amount: 0, currency: 'gbp', shipping_address: { name: '', line1: '', city: '', postcode: '', country: 'GB' }, admin_notes: '' })} className="bg-emerald-500 hover:bg-emerald-600">
              <Plus className="w-4 h-4 mr-2" /> Create Order
            </Button>
          </div>

          {orders.length === 0 ? (
            <div className="bg-white rounded-xl border p-8 text-center">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No orders yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => {
                const checklist = order.checklist || {};
                const statusColor = {
                  pending: 'bg-amber-100 text-amber-700',
                  paid: 'bg-emerald-100 text-emerald-700',
                  processing: 'bg-blue-100 text-blue-700',
                  shipped: 'bg-purple-100 text-purple-700',
                  delivered: 'bg-teal-100 text-teal-700'
                }[order.status] || 'bg-slate-100 text-slate-700';

                const updateChecklist = async (key, value) => {
                  const newChecklist = { ...checklist, [key]: value };
                  try {
                    const r = await fetch(`${API_URL}/api/admin/orders/${order.order_id}`, {
                      method: 'PATCH',
                      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                      body: JSON.stringify({ checklist: newChecklist })
                    });
                    if (r.ok) { toast.success(`Updated — email sent to customer`); fetchAll(); }
                  } catch (e) { toast.error('Failed to update'); }
                };

                const updateField = async (field, value) => {
                  try {
                    await fetch(`${API_URL}/api/admin/orders/${order.order_id}`, {
                      method: 'PATCH',
                      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                      body: JSON.stringify({ [field]: value, checklist })
                    });
                    fetchAll();
                  } catch (e) { toast.error('Failed to save'); }
                };

                return (
                  <div key={order.order_id} className="bg-white rounded-xl border overflow-hidden">
                    {/* Order Header */}
                    <div className="p-4 flex items-start justify-between border-b bg-slate-50">
                      <div>
                        <h4 className="font-bold text-slate-800">{order.product_title || order.book_title}</h4>
                        <p className="text-sm text-slate-600">{order.customer_name || order.customer_email}</p>
                        <p className="text-xs text-slate-400">#{order.order_id?.slice(-8)} | {new Date(order.created_at).toLocaleString()}</p>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor}`}>{order.status}</span>
                        <p className="text-lg font-bold text-slate-800 mt-1">£{(order.amount || 0).toFixed(2)}</p>
                      </div>
                    </div>

                    <div className="p-4 grid md:grid-cols-2 gap-4">
                      {/* Shipping Address */}
                      {order.shipping_address && (
                        <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
                          <p className="font-medium text-slate-700 mb-1">Ship To:</p>
                          <p>{order.shipping_address.name}</p>
                          <p>{order.shipping_address.line1}</p>
                          {order.shipping_address.line2 && <p>{order.shipping_address.line2}</p>}
                          <p>{order.shipping_address.city}, {order.shipping_address.postcode}</p>
                          <p>{order.shipping_address.country}</p>
                        </div>
                      )}

                      {/* Order Checklist */}
                      <div className="space-y-3">
                        <p className="font-medium text-slate-700 text-sm">Order Checklist:</p>
                        {[
                          { key: 'payment_confirmed', label: 'Payment Confirmed', color: 'emerald' },
                          { key: 'order_processed', label: 'Order Processed', color: 'blue' },
                          { key: 'order_shipped', label: 'Order Shipped', color: 'purple' },
                          { key: 'order_delivered', label: 'Order Delivered', color: 'teal' }
                        ].map(step => (
                          <label key={step.key} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${checklist[step.key] ? `bg-${step.color}-50` : 'hover:bg-slate-50'}`}>
                            <input
                              type="checkbox"
                              checked={!!checklist[step.key]}
                              onChange={(e) => updateChecklist(step.key, e.target.checked)}
                              className={`w-5 h-5 rounded border-2 text-${step.color}-500 focus:ring-${step.color}-500`}
                            />
                            <span className={`text-sm font-medium ${checklist[step.key] ? `text-${step.color}-700` : 'text-slate-600'}`}>
                              {step.label}
                            </span>
                            {checklist[step.key] && <Check className="w-4 h-4 text-emerald-500 ml-auto" />}
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Tracking & Delivery */}
                    <div className="px-4 pb-4 grid md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Tracking Number</label>
                        <input
                          className="w-full px-3 py-1.5 border rounded-lg text-sm"
                          defaultValue={order.tracking_number || ''}
                          placeholder="Enter tracking..."
                          onBlur={(e) => { if (e.target.value !== (order.tracking_number || '')) updateField('tracking_number', e.target.value); }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Expected Delivery</label>
                        <input
                          type="date"
                          className="w-full px-3 py-1.5 border rounded-lg text-sm"
                          defaultValue={order.expected_delivery || ''}
                          onChange={(e) => updateField('expected_delivery', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Admin Notes</label>
                        <input
                          className="w-full px-3 py-1.5 border rounded-lg text-sm"
                          defaultValue={order.admin_notes || ''}
                          placeholder="Internal notes..."
                          onBlur={(e) => { if (e.target.value !== (order.admin_notes || '')) updateField('admin_notes', e.target.value); }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Create Order Modal */}
      {newOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-bold">Create Manual Order</h3>
              <Button variant="ghost" size="sm" onClick={() => setNewOrder(null)}><X className="w-5 h-5" /></Button>
            </div>
            <div className="p-6 space-y-4">
              <div><label className="block text-xs font-medium mb-1">Product/Description *</label><Input value={newOrder.product_title} onChange={e => setNewOrder({...newOrder, product_title: e.target.value})} placeholder="e.g., Book Bundle" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-xs font-medium mb-1">Customer Name *</label><Input value={newOrder.customer_name} onChange={e => setNewOrder({...newOrder, customer_name: e.target.value})} /></div>
                <div><label className="block text-xs font-medium mb-1">Customer Email</label><Input value={newOrder.customer_email} onChange={e => setNewOrder({...newOrder, customer_email: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-xs font-medium mb-1">Amount (£)</label><Input type="number" step="0.01" value={newOrder.amount} onChange={e => setNewOrder({...newOrder, amount: parseFloat(e.target.value) || 0})} /></div>
                <div><label className="block text-xs font-medium mb-1">Status</label>
                  <select value={newOrder.status || 'pending'} onChange={e => setNewOrder({...newOrder, status: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm">
                    <option value="pending">Pending</option><option value="paid">Paid</option><option value="processing">Processing</option><option value="shipped">Shipped</option>
                  </select>
                </div>
              </div>
              <div><label className="block text-xs font-medium mb-1">Shipping Address</label><Input value={newOrder.shipping_address?.line1 || ''} onChange={e => setNewOrder({...newOrder, shipping_address: {...newOrder.shipping_address, line1: e.target.value}})} placeholder="Address line 1" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Input value={newOrder.shipping_address?.city || ''} onChange={e => setNewOrder({...newOrder, shipping_address: {...newOrder.shipping_address, city: e.target.value}})} placeholder="City" /></div>
                <div><Input value={newOrder.shipping_address?.postcode || ''} onChange={e => setNewOrder({...newOrder, shipping_address: {...newOrder.shipping_address, postcode: e.target.value}})} placeholder="Postcode" /></div>
              </div>
              <div><label className="block text-xs font-medium mb-1">Notes</label><Input value={newOrder.admin_notes || ''} onChange={e => setNewOrder({...newOrder, admin_notes: e.target.value})} placeholder="Internal notes" /></div>
            </div>
            <div className="p-4 border-t flex gap-2">
              <Button variant="outline" onClick={() => setNewOrder(null)} className="flex-1">Cancel</Button>
              <Button onClick={async () => {
                if (!newOrder.product_title || !newOrder.customer_name) { toast.error('Product and customer name required'); return; }
                try {
                  const r = await fetch(`${API_URL}/api/admin/orders/create`, {
                    method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                    body: JSON.stringify({...newOrder, shipping_address: {...newOrder.shipping_address, name: newOrder.customer_name}})
                  });
                  if (r.ok) { toast.success('Order created!'); setNewOrder(null); fetchAll(); }
                } catch (e) { toast.error('Failed'); }
              }} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white">Create Order</Button>
            </div>
          </div>
        </div>
      )}

      {/* Products */}
      {subTab === 'products' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-bold text-slate-800">Custom Products</h3>
            <Button onClick={() => setEditingProduct({ title: '', description: '', category: 'Books', price: 0, currency: 'gbp', image_url: '', in_stock: true, is_active: true })} className="bg-emerald-500 hover:bg-emerald-600">
              <Plus className="w-4 h-4 mr-2" /> Add Product
            </Button>
          </div>
          <p className="text-sm text-slate-500">Books with "shop enabled" also appear. Add other products here (posters, t-shirts, etc.).</p>
          {products.length === 0 ? (
            <div className="bg-white rounded-xl border p-8 text-center">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No custom products yet</p>
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {products.map(p => (
                <div key={p.product_id} className="bg-white rounded-xl border p-4 flex gap-4">
                  <div className="w-16 h-16 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                    {p.image_url ? <img src={p.image_url} alt="" className="w-full h-full object-cover" /> : <Package className="w-8 h-8 text-slate-300 m-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-slate-800 truncate">{p.title}</h4>
                    <p className="text-sm text-slate-500">{p.category} — £{p.price}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => setEditingProduct({...p})}><PenLine className="w-4 h-4" /></Button>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteProduct(p.product_id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Categories */}
      {subTab === 'categories' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-bold text-slate-800">Shop Categories</h3>
            <Button onClick={() => setEditingCategory({ name: '', sort_order: categories.length })} className="bg-emerald-500 hover:bg-emerald-600">
              <Plus className="w-4 h-4 mr-2" /> Add Category
            </Button>
          </div>
          <div className="space-y-2">
            {categories.map(c => (
              <div key={c.slug} className="bg-white rounded-xl border p-4 flex items-center justify-between">
                <div>
                  <h4 className="font-bold text-slate-800">{c.name}</h4>
                  <p className="text-xs text-slate-500">Slug: {c.slug}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => setEditingCategory({...c})}><PenLine className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Shipping Rates */}
      {subTab === 'shipping' && shippingConfig && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-bold text-slate-800">Shipping Rates</h3>
              <p className="text-sm text-slate-500">Configure delivery costs by region</p>
            </div>
            <Button onClick={saveShippingRates} disabled={savingShipping} className="bg-emerald-500 hover:bg-emerald-600">
              {savingShipping ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />} Save Rates
            </Button>
          </div>

          <div className="bg-white rounded-xl border overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Region</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Display Name</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Cost (£)</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(shippingConfig.rates || {}).map(([region, rate]) => (
                  <tr key={region} className="border-b hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-sm text-slate-500">{region}</td>
                    <td className="px-4 py-3">
                      <Input value={rate.name || ''} onChange={(e) => updateShippingRate(region, 'name', e.target.value)} className="text-sm h-8" />
                    </td>
                    <td className="px-4 py-3">
                      <Input type="number" step="0.01" value={rate.cost ?? ''} onChange={(e) => updateShippingRate(region, 'cost', e.target.value)} className="text-sm h-8 w-24" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border p-4">
              <label className="block text-sm font-medium mb-1">Free Shipping Threshold (£)</label>
              <Input type="number" step="0.01" value={shippingConfig.free_shipping_threshold || ''} onChange={(e) => setShippingConfig({...shippingConfig, free_shipping_threshold: parseFloat(e.target.value) || 0})} />
              <p className="text-xs text-slate-500 mt-1">Orders above this amount get free delivery. Set to 0 to disable.</p>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <label className="block text-sm font-medium mb-1">Bulk Order Limit</label>
              <Input type="number" value={shippingConfig.bulk_limit || 10} onChange={(e) => setShippingConfig({...shippingConfig, bulk_limit: parseInt(e.target.value) || 10})} />
              <p className="text-xs text-slate-500 mt-1">Orders with this many copies of one item will be directed to contact you.</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <label className="block text-sm font-medium mb-1">Bulk Order Message</label>
            <Input value={shippingConfig.bulk_message || ''} onChange={(e) => setShippingConfig({...shippingConfig, bulk_message: e.target.value})} />
          </div>
        </div>
      )}

      {/* Product Editor Modal */}
      {editingProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-bold">{editingProduct.product_id ? 'Edit Product' : 'New Product'}</h3>
              <Button variant="ghost" size="sm" onClick={() => setEditingProduct(null)}><X className="w-5 h-5" /></Button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title *</label>
                <Input value={editingProduct.title || ''} onChange={(e) => setEditingProduct({...editingProduct, title: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea value={editingProduct.description || ''} onChange={(e) => setEditingProduct({...editingProduct, description: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm min-h-[80px]" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Price (£)</label>
                  <Input type="number" step="0.01" value={editingProduct.price || ''} onChange={(e) => setEditingProduct({...editingProduct, price: parseFloat(e.target.value) || 0})} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Category</label>
                  <select value={editingProduct.category || ''} onChange={(e) => setEditingProduct({...editingProduct, category: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm">
                    {categories.map(c => <option key={c.slug} value={c.name}>{c.name}</option>)}
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Product Image</label>
                <div className="flex gap-2 items-end">
                  {editingProduct.image_url && (
                    <div className="w-16 h-16 rounded-lg overflow-hidden border flex-shrink-0">
                      <img src={editingProduct.image_url} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex-1 space-y-2">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      id="product-image-upload"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const formData = new FormData();
                        formData.append('file', file);
                        formData.append('type', 'product');
                        try {
                          const r = await fetch(`${API_URL}/api/admin/upload-image`, {
                            method: 'POST',
                            headers: getAuthHeaders(),
                            body: formData
                          });
                          if (r.ok) {
                            const data = await r.json();
                            setEditingProduct({...editingProduct, image_url: data.url});
                            toast.success('Image uploaded!');
                          }
                        } catch (err) { toast.error('Upload failed'); }
                        e.target.value = '';
                      }}
                    />
                    <label htmlFor="product-image-upload" className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500 text-white rounded-lg cursor-pointer hover:bg-emerald-600 text-sm">
                      <Upload className="w-4 h-4" /> Upload Image
                    </label>
                    <Input value={editingProduct.image_url || ''} onChange={(e) => setEditingProduct({...editingProduct, image_url: e.target.value})} placeholder="Or paste image URL..." className="text-sm" />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={editingProduct.is_active !== false} onChange={(e) => setEditingProduct({...editingProduct, is_active: e.target.checked})} className="rounded" />
                <label className="text-sm">Active (visible in shop)</label>
              </div>
            </div>
            <div className="p-4 border-t flex gap-2">
              <Button variant="outline" onClick={() => setEditingProduct(null)} className="flex-1">Cancel</Button>
              <Button onClick={saveProduct} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white">Save</Button>
            </div>
          </div>
        </div>
      )}

      {/* Category Editor Modal */}
      {editingCategory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-bold">{editingCategory.slug ? 'Edit Category' : 'New Category'}</h3>
              <Button variant="ghost" size="sm" onClick={() => setEditingCategory(null)}><X className="w-5 h-5" /></Button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Category Name *</label>
                <Input value={editingCategory.name || ''} onChange={(e) => setEditingCategory({...editingCategory, name: e.target.value})} placeholder="e.g., Posters, T-Shirts" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Sort Order</label>
                <Input type="number" value={editingCategory.sort_order || 0} onChange={(e) => setEditingCategory({...editingCategory, sort_order: parseInt(e.target.value) || 0})} />
              </div>
            </div>
            <div className="p-4 border-t flex gap-2">
              <Button variant="outline" onClick={() => setEditingCategory(null)} className="flex-1">Cancel</Button>
              <Button onClick={saveCategory} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white">Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// GDPR Panel
const GdprPanel = ({ getAuthHeaders }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lookupEmail, setLookupEmail] = useState('');
  const [userData, setUserData] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);

  useEffect(() => { fetchLogs(); }, []);

  const fetchLogs = async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/gdpr/log`, { headers: getAuthHeaders() });
      if (r.ok) setLogs(await r.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const lookupUser = async () => {
    if (!lookupEmail.includes('@')) { toast.error('Enter a valid email'); return; }
    setLookingUp(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/gdpr/user/${encodeURIComponent(lookupEmail)}`, { headers: getAuthHeaders() });
      if (r.ok) setUserData(await r.json());
      else { toast.error('User not found'); setUserData(null); }
    } catch (e) { toast.error('Lookup failed'); }
    setLookingUp(false);
  };

  const deleteUserData = async (email) => {
    if (!window.confirm(`Permanently delete ALL data for ${email}? This cannot be undone.`)) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/gdpr/user/${encodeURIComponent(email)}`, {
        method: 'DELETE', headers: getAuthHeaders()
      });
      if (r.ok) { toast.success('User data deleted'); setUserData(null); fetchLogs(); }
      else toast.error('Failed to delete');
    } catch (e) { toast.error('Deletion failed'); }
  };

  const exportUserData = (data) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `user-data-${data?.profile?.email || 'export'}.json`; a.click();
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-bold text-slate-800">GDPR Data Management</h3>
        <p className="text-sm text-slate-500">Look up, export, or delete user data. All actions are logged.</p>
      </div>

      {/* User Lookup */}
      <div className="bg-white rounded-xl border p-5">
        <h4 className="font-bold text-slate-800 mb-3">User Data Lookup</h4>
        <div className="flex gap-2">
          <Input value={lookupEmail} onChange={(e) => setLookupEmail(e.target.value)} placeholder="user@gmail.com" className="max-w-sm" />
          <Button onClick={lookupUser} disabled={lookingUp} className="bg-emerald-500 hover:bg-emerald-600">
            {lookingUp ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />} Look Up
          </Button>
        </div>

        {userData && (
          <div className="mt-4 bg-slate-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-bold text-slate-800">{userData.profile?.name}</p>
                <p className="text-sm text-slate-500">{userData.profile?.email} | Tier: {userData.profile?.tier}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => exportUserData(userData)}>
                  <Download className="w-4 h-4 mr-1" /> Export
                </Button>
                <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => deleteUserData(userData.profile?.email)}>
                  <Trash2 className="w-4 h-4 mr-1" /> Delete All
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              {[
                ['Bookmarks', userData.bookmarks?.length || 0],
                ['Journals', userData.journal_entries?.length || 0],
                ['Orders', userData.orders?.length || 0],
                ['Payments', userData.payments?.length || 0],
              ].map(([label, count]) => (
                <div key={label} className="bg-white rounded-lg p-2 border">
                  <p className="text-lg font-bold text-slate-800">{count}</p>
                  <p className="text-xs text-slate-500">{label}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Audit Log */}
      <div className="bg-white rounded-xl border p-5">
        <h4 className="font-bold text-slate-800 mb-3">GDPR Audit Log ({logs.length})</h4>
        {logs.length === 0 ? (
          <p className="text-slate-500 text-sm text-center py-4">No GDPR actions recorded yet</p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {logs.map((log, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg text-sm">
                <div>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium mr-2 ${
                    log.action === 'data_deletion' || log.action === 'admin_data_deletion' ? 'bg-red-100 text-red-700' :
                    log.action === 'data_access' ? 'bg-blue-100 text-blue-700' :
                    'bg-amber-100 text-amber-700'
                  }`}>{log.action.replace(/_/g, ' ')}</span>
                  <span className="text-slate-600">{log.email}</span>
                  {log.admin && <span className="text-xs text-slate-400 ml-2">by {log.admin}</span>}
                </div>
                <span className="text-xs text-slate-400">{log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-slate-400">Users can access, download, rectify, and delete their data from the "My Data & Privacy" page in the footer. All actions are logged here automatically.</p>
    </div>
  );
};


// Analytics Panel
const AnalyticsPanel = ({ getAuthHeaders }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/admin/analytics`, { headers: getAuthHeaders() })
      .then(r => r.json()).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>;
  if (!data) return <p className="text-slate-500 text-center py-12">Failed to load analytics</p>;

  const totalPaid = (data.users.by_tier?.basic || 0) + (data.users.by_tier?.premium || 0) + (data.users.by_tier?.faith_builder || 0);
  const maxTier = Math.max(data.users.by_tier?.free || 0, data.users.by_tier?.basic || 0, data.users.by_tier?.premium || 0, data.users.by_tier?.faith_builder || 0, 1);

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-bold text-slate-800">Site Analytics</h3>
      
      {/* Top Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Users', value: data.users.total, color: 'emerald', icon: '👥' },
          { label: 'Paid Subscribers', value: totalPaid, color: 'blue', icon: '💎' },
          { label: 'Books', value: data.books.total, color: 'purple', icon: '📚' },
          { label: 'Newsletter', value: data.newsletter.subscribers, color: 'amber', icon: '📧' },
        ].map((s, i) => (
          <div key={i} className={`bg-gradient-to-br from-${s.color}-50 to-white rounded-xl border border-${s.color}-200 p-5`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-2xl">{s.icon}</span>
              <span className={`text-3xl font-bold text-${s.color}-600`}>{s.value}</span>
            </div>
            <p className="text-sm text-slate-600">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Subscription Breakdown with Visual Bars */}
      <div className="bg-white rounded-xl border p-5">
        <h4 className="font-bold text-slate-800 mb-4">Subscription Breakdown</h4>
        <div className="space-y-3">
          {[
            { name: 'Explorer (Free)', count: data.users.by_tier?.free || 0, color: 'bg-emerald-500' },
            { name: 'Adventurer', count: data.users.by_tier?.basic || 0, color: 'bg-sky-500' },
            { name: 'Champion', count: data.users.by_tier?.premium || 0, color: 'bg-amber-500' },
            { name: 'Faith Builder', count: data.users.by_tier?.faith_builder || 0, color: 'bg-purple-500' },
          ].map((tier, i) => (
            <div key={i}>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium text-slate-700">{tier.name}</span>
                <span className="font-bold text-slate-800">{tier.count}</span>
              </div>
              <div className="h-4 bg-slate-100 rounded-full overflow-hidden">
                <div className={`h-full ${tier.color} rounded-full transition-all`} style={{ width: `${(tier.count / maxTier) * 100}%`, minWidth: tier.count > 0 ? '8px' : '0' }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Activity Grid */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Reading Stats */}
        <div className="bg-white rounded-xl border p-5">
          <h4 className="font-bold text-slate-800 mb-3">Reading Activity</h4>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Pages Turned</span>
              <span className="text-xl font-bold text-purple-600">{(data.books.total_pages_turned || 0).toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Books Completed</span>
              <span className="text-xl font-bold text-emerald-600">{data.books.total_books_completed}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Avg Reading Streak</span>
              <span className="text-xl font-bold text-amber-600">{data.books.avg_reading_streak} days</span>
            </div>
          </div>
        </div>

        {/* Quiz Stats */}
        <div className="bg-white rounded-xl border p-5">
          <h4 className="font-bold text-slate-800 mb-3">Quiz Activity</h4>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Total Attempts</span>
              <span className="text-xl font-bold text-purple-600">{data.quizzes.total_attempts}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Average Score</span>
              <div className="flex items-center gap-2">
                <div className="w-24 h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${data.quizzes.avg_score}%` }} />
                </div>
                <span className="text-sm font-bold text-emerald-600">{data.quizzes.avg_score}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Shop Stats */}
        <div className="bg-white rounded-xl border p-5">
          <h4 className="font-bold text-slate-800 mb-3">Shop</h4>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-600">Total Orders</span>
            <span className="text-xl font-bold text-blue-600">{data.shop.total_orders}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">Revenue</span>
            <span className="text-xl font-bold text-emerald-600">£{data.shop.total_revenue.toLocaleString()}</span>
          </div>
        </div>

        {/* Engagement */}
        <div className="bg-white rounded-xl border p-5">
          <h4 className="font-bold text-slate-800 mb-3">Engagement</h4>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Readathon Signups', value: data.readathons.total_signups, color: 'text-blue-600' },
              { label: 'Journal Entries', value: data.readathons.total_journals, color: 'text-purple-600' },
              { label: 'Testimonials', value: data.engagement.testimonials, color: 'text-amber-600' },
              { label: 'Volunteer Apps', value: data.engagement.volunteer_applications, color: 'text-emerald-600' },
              { label: 'Messages', value: data.engagement.contact_messages, color: 'text-slate-600' },
              { label: 'Emails Sent', value: data.newsletter.emails_sent, color: 'text-blue-600' },
            ].map((s, i) => (
              <div key={i} className="text-center p-2 bg-slate-50 rounded-lg">
                <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
                <p className="text-xs text-slate-500">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};


// Invoices Panel
const InvoicesPanel = ({ getAuthHeaders }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  useEffect(() => { fetchInvoices(); }, []);

  const fetchInvoices = async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/invoices`, { headers: getAuthHeaders() });
      if (r.ok) setInvoices(await r.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const newInvoice = () => {
    setEditing({
      invoice_id: `inv_${Date.now()}`,
      invoice_number: '',
      date: new Date().toISOString().slice(0, 10),
      customer_name: '',
      customer_email: '',
      customer_address: '',
      currency: 'GBP',
      items: [{ description: '', price: 0, units: 1 }],
      discount: 0,
      tax: 0,
      notes: '',
      status: 'draft'
    });
  };

  const saveInvoice = async () => {
    if (!editing?.customer_name) { toast.error('Customer name required'); return; }
    try {
      const r = await fetch(`${API_URL}/api/admin/invoices/${editing.invoice_id}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(editing)
      });
      if (r.ok) { toast.success('Invoice saved!'); setEditing(null); fetchInvoices(); }
    } catch (e) { toast.error('Failed to save'); }
  };

  const sendInvoice = async (invId) => {
    try {
      const r = await fetch(`${API_URL}/api/admin/invoices/${invId}/send`, { method: 'POST', headers: getAuthHeaders() });
      if (r.ok) { toast.success('Invoice sent!'); fetchInvoices(); } else { const err = await r.json(); toast.error(err.detail || 'Failed'); }
    } catch (e) { toast.error('Failed to send'); }
  };

  const updateInvoiceStatus = async (invId, status) => {
    try {
      await fetch(`${API_URL}/api/admin/invoices/${invId}/status`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      toast.success(`Invoice marked as ${status}`);
      fetchInvoices();
    } catch (e) { toast.error('Failed to update'); }
  };

  const outstanding = invoices.filter(i => i.status !== 'paid');
  const paidInvoices = invoices.filter(i => i.status === 'paid');

  const subtotal = editing ? editing.items.reduce((sum, i) => sum + (parseFloat(i.price) || 0) * (parseInt(i.units) || 1), 0) : 0;
  const total = subtotal - (parseFloat(editing?.discount) || 0) + (parseFloat(editing?.tax) || 0);
  const currencySymbol = editing?.currency === 'NGN' ? '₦' : '£';

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-bold text-slate-800">Invoices</h3>
          <p className="text-sm text-slate-500">
            {outstanding.length} outstanding | {paidInvoices.length} paid | {invoices.length} total
          </p>
        </div>
        <Button onClick={newInvoice} className="bg-emerald-500 hover:bg-emerald-600"><Plus className="w-4 h-4 mr-2" /> New Invoice</Button>
      </div>

      {invoices.length === 0 && !editing ? (
        <div className="bg-white rounded-xl border p-8 text-center"><Package className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">No invoices yet</p></div>
      ) : (
        <div className="space-y-3">
          {invoices.map(inv => (
            <div key={inv.invoice_id} className="bg-white rounded-xl border p-4 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="font-bold text-slate-800">{inv.invoice_number || 'Draft'}</h4>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${inv.status === 'sent' ? 'bg-blue-100 text-blue-700' : inv.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{inv.status}</span>
                </div>
                <p className="text-sm text-slate-600">{inv.customer_name} — {inv.currency === 'NGN' ? '₦' : '£'}{(inv.total || 0).toLocaleString()}</p>
                <p className="text-xs text-slate-400">{inv.date}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setEditing({...inv})}><PenLine className="w-4 h-4" /></Button>
                <Button size="sm" variant="outline" onClick={() => sendInvoice(inv.invoice_id)}><Mail className="w-4 h-4" /></Button>
                <Button size="sm" variant="outline" onClick={() => window.open(`/invoice/${inv.invoice_id}`, '_blank')}><Eye className="w-4 h-4" /></Button>
                {inv.status !== 'paid' && (
                  <Button size="sm" onClick={() => updateInvoiceStatus(inv.invoice_id, 'paid')} className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs">Mark Paid</Button>
                )}
                {inv.status === 'paid' && (
                  <Button size="sm" variant="outline" onClick={() => updateInvoiceStatus(inv.invoice_id, 'draft')} className="text-xs">Unpay</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Invoice Editor Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
              <h3 className="font-bold">{editing.invoice_number || 'New Invoice'}</h3>
              <Button variant="ghost" size="sm" onClick={() => setEditing(null)}><X className="w-5 h-5" /></Button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div><label className="block text-xs font-medium mb-1">Date</label><Input type="date" value={editing.date || ''} onChange={e => setEditing({...editing, date: e.target.value})} /></div>
                <div><label className="block text-xs font-medium mb-1">Invoice #</label><Input value={editing.invoice_number || ''} onChange={e => setEditing({...editing, invoice_number: e.target.value})} placeholder="Auto-generated" /></div>
                <div><label className="block text-xs font-medium mb-1">Currency</label>
                  <select value={editing.currency || 'GBP'} onChange={e => setEditing({...editing, currency: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm">
                    <option value="GBP">GBP (£)</option><option value="NGN">NGN (₦)</option><option value="USD">USD ($)</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-xs font-medium mb-1">Customer Name *</label><Input value={editing.customer_name || ''} onChange={e => setEditing({...editing, customer_name: e.target.value})} /></div>
                <div><label className="block text-xs font-medium mb-1">Customer Email</label><Input value={editing.customer_email || ''} onChange={e => setEditing({...editing, customer_email: e.target.value})} /></div>
              </div>
              <div><label className="block text-xs font-medium mb-1">Customer Address</label><textarea value={editing.customer_address || ''} onChange={e => setEditing({...editing, customer_address: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm" rows={2} /></div>

              {/* Line Items */}
              <div>
                <div className="flex justify-between items-center mb-2"><label className="text-sm font-bold">Items</label>
                  <Button size="sm" variant="outline" onClick={() => setEditing({...editing, items: [...editing.items, {description: '', price: 0, units: 1}]})}><Plus className="w-3 h-3 mr-1" /> Add</Button>
                </div>
                <div className="space-y-2">
                  {editing.items.map((item, i) => (
                    <div key={i} className="flex gap-2 items-end">
                      <div className="flex-1"><Input value={item.description} onChange={e => { const items = [...editing.items]; items[i].description = e.target.value; setEditing({...editing, items}); }} placeholder="Description" className="text-sm" /></div>
                      <div className="w-24"><Input type="number" step="0.01" value={item.price} onChange={e => { const items = [...editing.items]; items[i].price = e.target.value; setEditing({...editing, items}); }} placeholder="Price" className="text-sm" /></div>
                      <div className="w-16"><Input type="number" value={item.units} onChange={e => { const items = [...editing.items]; items[i].units = e.target.value; setEditing({...editing, items}); }} placeholder="Qty" className="text-sm" /></div>
                      <div className="w-20 text-right font-bold text-sm pt-2">{currencySymbol}{((parseFloat(item.price) || 0) * (parseInt(item.units) || 1)).toLocaleString()}</div>
                      {editing.items.length > 1 && <button onClick={() => { const items = editing.items.filter((_, j) => j !== i); setEditing({...editing, items}); }} className="text-red-500 p-1"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-xs font-medium mb-1">Discount</label><Input type="number" step="0.01" value={editing.discount || ''} onChange={e => setEditing({...editing, discount: e.target.value})} /></div>
                <div><label className="block text-xs font-medium mb-1">Tax</label><Input type="number" step="0.01" value={editing.tax || ''} onChange={e => setEditing({...editing, tax: e.target.value})} /></div>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 text-right space-y-1">
                <p className="text-sm text-slate-600">Subtotal: {currencySymbol}{subtotal.toLocaleString()}</p>
                {(parseFloat(editing.discount) || 0) > 0 && <p className="text-sm text-red-600">Discount: -{currencySymbol}{parseFloat(editing.discount).toLocaleString()}</p>}
                {(parseFloat(editing.tax) || 0) > 0 && <p className="text-sm text-slate-600">Tax: +{currencySymbol}{parseFloat(editing.tax).toLocaleString()}</p>}
                <p className="text-xl font-bold text-slate-800">Total: {currencySymbol}{total.toLocaleString()}</p>
              </div>

              {/* Bank details preview */}
              <div className={`p-4 rounded-lg border text-sm ${editing.currency === 'NGN' ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
                <p className="font-bold mb-1">{editing.currency === 'NGN' ? 'Nigeria Bank Details' : 'UK Bank Details'}</p>
                {editing.currency === 'NGN' ? (
                  <><p>Account Name: Jadesola Ayoola</p><p>Bank: Kuda Bank</p><p>Account Number: 2063547946</p></>
                ) : (
                  <><p>Account Name: Creassion Resources</p><p>Account Number: 67455779</p><p>Sort Code: 08-92-99</p></>
                )}
              </div>

              <div><label className="block text-xs font-medium mb-1">Notes</label><textarea value={editing.notes || ''} onChange={e => setEditing({...editing, notes: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm" rows={2} placeholder="Thank you for your support..." /></div>
            </div>
            <div className="p-4 border-t flex gap-2 sticky bottom-0 bg-white">
              <Button variant="outline" onClick={() => setEditing(null)} className="flex-1">Cancel</Button>
              <Button onClick={saveInvoice} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white">Save Invoice</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default AdminDashboard;
